import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import Gate from "./Gate";
import { socket, api, SERVER_URL, getGate, clearGate, getToken, setToken, clearToken, connectSocket, disconnectSocket, uploadFile, mediaSrc, MAX_FILE_BYTES } from "./socket";

/* ─── GOOGLE FONTS ─── */
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=Instrument+Serif:ital@0;1&display=swap";
document.head.appendChild(fontLink);

/* ─── DESIGN SYSTEM ─── */
const T = {
  blue50:"#EFF6FF",blue100:"#DBEAFE",blue200:"#BFDBFE",blue400:"#60A5FA",
  blue500:"#3B82F6",blue600:"#2563EB",blue700:"#1D4ED8",
  slate50:"#F8FAFC",slate100:"#F1F5F9",slate200:"#E2E8F0",slate300:"#CBD5E1",
  slate400:"#94A3B8",slate500:"#64748B",slate600:"#475569",slate700:"#334155",
  slate800:"#1E293B",slate900:"#0F172A",white:"#FFFFFF",
  success:"#10B981",successBg:"#ECFDF5",danger:"#EF4444",dangerBg:"#FEF2F2",
  warning:"#F59E0B",warningBg:"#FFFBEB",teal:"#0D9488",tealBg:"#F0FDFA",
  violet:"#7C3AED",violetBg:"#F5F3FF",rose:"#E11D48",roseBg:"#FFF1F2",
};

const ROLE_CONFIG = {
  Member:  { color: T.blue600,  bg: T.blue50,   icon: "👤" },
  Other:   { color: T.slate500, bg: T.slate100, icon: "👤" },
};

const LANGUAGES = [
  { code: "auto", label: "Auto-detect" },
  { code: "en",   label: "English" },
  { code: "ta",   label: "Tamil" },
  { code: "hi",   label: "Hindi" },
  { code: "te",   label: "Telugu" },
  { code: "kn",   label: "Kannada" },
  { code: "ml",   label: "Malayalam" },
  { code: "bn",   label: "Bengali" },
  { code: "gu",   label: "Gujarati" },
  { code: "mr",   label: "Marathi" },
  { code: "pa",   label: "Punjabi" },
  { code: "ur",   label: "Urdu" },
  { code: "ar",   label: "Arabic" },
  { code: "zh",   label: "Chinese" },
  { code: "ja",   label: "Japanese" },
  { code: "ko",   label: "Korean" },
  { code: "fr",   label: "French" },
  { code: "de",   label: "German" },
  { code: "es",   label: "Spanish" },
  { code: "pt",   label: "Portuguese" },
  { code: "ru",   label: "Russian" },
  { code: "it",   label: "Italian" },
  { code: "tr",   label: "Turkish" },
];

/* ══════════════════════════════════════════════════════════
   LANGUAGE CONFIG — Speech recognition + Translation
══════════════════════════════════════════════════════════ */

// Each language: what BCP-47 codes to try for speech recognition
// and what Google Translate code to use
const LANG_CONFIG = {
  "ta": { speech: ["ta-IN"],               gt: "ta", label: "Tamil"   },
  "en": { speech: ["en-IN","en-US","en-GB"],gt: "en", label: "English" },
  "hi": { speech: ["hi-IN"],               gt: "hi", label: "Hindi"   },
  "te": { speech: ["te-IN"],               gt: "te", label: "Telugu"  },
  "kn": { speech: ["kn-IN"],               gt: "kn", label: "Kannada" },
  "ml": { speech: ["ml-IN"],               gt: "ml", label: "Malayalam"},
  "mr": { speech: ["mr-IN"],               gt: "mr", label: "Marathi" },
  "bn": { speech: ["bn-IN","bn-BD"],        gt: "bn", label: "Bengali" },
};

// Script detection helpers
const isTamil   = t => /[\u0B80-\u0BFF]/.test(t);
const isHindi   = t => /[\u0900-\u097F]/.test(t);
const isTelugu  = t => /[\u0C00-\u0C7F]/.test(t);
const isKannada = t => /[\u0C80-\u0CFF]/.test(t);
const isMalayalam=t => /[\u0D00-\u0D7F]/.test(t);
const isIndian  = t => /[\u0900-\u0DFF]/.test(t);

// Detect source language code from script
function detectScriptLang(text) {
  if (isTamil(text))    return "ta";
  if (isHindi(text))    return "hi";
  if (isTelugu(text))   return "te";
  if (isKannada(text))  return "kn";
  if (isMalayalam(text))return "ml";
  return null;
}

/* ── Google Translate (browser-direct, free) ── */
async function gtTranslate(text, sl, tl) {
  try {
    const url = "https://translate.googleapis.com/translate_a/single" +
      "?client=gtx&sl=" + encodeURIComponent(sl) +
      "&tl=" + encodeURIComponent(tl) +
      "&dt=t&q=" + encodeURIComponent(text);
    const r = await fetch(url);
    if (!r.ok) return null;
    const d = await r.json();
    const result = (d[0] || []).map(x => x?.[0] || "").join("").trim();
    return result || null;
  } catch { return null; }
}

/* ── Main translation function ── */
async function translateWithClaude(text, targetLang) {
  if (!text || !text.trim()) return text;
  if (!targetLang || targetLang === "auto") return text;

  const input = text.trim();
  const tl = LANG_CONFIG[targetLang]?.gt || targetLang;
  const lower = input.toLowerCase();

  try {
    // Step 1: Detect source language from Unicode script
    const scriptLang = detectScriptLang(input);

    // Step 2: Build source language candidates in order of confidence
    let candidates = [];

    if (scriptLang) {
      // Text is in a known Indian script — translate directly
      candidates = [scriptLang, "auto"];
    } else if (targetLang === "en") {
      // Target English → speaker is non-English (Tamil most common in India)
      candidates = ["ta", "hi", "te", "kn", "ml", "auto"];
    } else if (targetLang === "ta") {
      candidates = ["en", "auto"];
    } else if (targetLang === "hi") {
      candidates = ["en", "ta", "auto"];
    } else if (targetLang === "te") {
      candidates = ["en", "ta", "auto"];
    } else {
      candidates = ["en", "auto"];
    }

    // Step 3: Try each candidate until we get a real translation
    for (const sl of candidates) {
      if (sl === tl) continue; // skip if source = target
      const result = await gtTranslate(input, sl, tl);
      if (result && result.toLowerCase() !== lower) {
        console.log(`✅ [${sl}→${tl}] "${input.slice(0,30)}" → "${result.slice(0,30)}"`);
        return result;
      }
    }
  } catch (err) {
    console.warn("Translation error:", err.message);
  }

  return text;
}

/* ── Speech recognition with multi-lang fallback ── */
function tryRecognizeWithLang(audioUrl, langCode) {
  return new Promise((resolve) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { resolve(""); return; }

    const audio = new Audio(audioUrl);
    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = langCode;

    let collected = "";
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try { rec.stop(); } catch(e) {}
      audio.pause();
      resolve(collected.trim());
    };

    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) collected += e.results[i][0].transcript + " ";
      }
    };
    rec.onerror = () => finish();
    rec.onend   = () => finish();
    audio.onended = () => setTimeout(finish, 600);
    audio.onerror = () => finish();

    try {
      rec.start();
      audio.currentTime = 0;
      audio.play().catch(() => finish());
    } catch(e) { finish(); }

    setTimeout(finish, 30000);
  });
}

async function transcribeAudioViaWebSpeech(audioUrl, targetLang) {
  // Determine which languages to try for recognition
  // The SPEAKER's language is what we recognize — usually opposite of target
  // But we try multiple to handle any case
  let langsToTry = [];

  if (targetLang === "en") {
    // Speaker is likely Tamil/Hindi/Telugu → try those
    langsToTry = ["ta-IN", "hi-IN", "te-IN", "en-IN"];
  } else if (targetLang === "ta") {
    // Speaker could be Tamil OR English
    langsToTry = ["ta-IN", "en-IN"];
  } else if (targetLang === "hi") {
    langsToTry = ["hi-IN", "en-IN", "ta-IN"];
  } else if (targetLang === "te") {
    langsToTry = ["te-IN", "en-IN", "ta-IN"];
  } else {
    langsToTry = ["en-IN", "ta-IN", "hi-IN"];
  }

  // Try each lang — return first non-empty result
  for (const lang of langsToTry) {
    const result = await tryRecognizeWithLang(audioUrl, lang);
    if (result && result.trim().length > 1) {
      console.log(`🎙 Recognized [${lang}]: "${result}"`);
      return result;
    }
  }
  return "";
}

/* ══════════════════════════════════════════════════════════
   VOICE TRANSCRIPTION + TRANSLATION
══════════════════════════════════════════════════════════ */
async function transcribeAndTranslate(voiceUrl, voiceBase64, voiceMime, liveTranscript, translateEnabled, targetLang) {
  const targetLabel = LANGUAGES.find(l => l.code === targetLang)?.label || targetLang;
  // Always translate when enabled — even to English (for people chatting across languages)
  const needsTranslation = translateEnabled && targetLang && targetLang !== "auto";

  let transcribedText = "";

  if (liveTranscript && liveTranscript.trim()) {
    transcribedText = liveTranscript.trim();
  } else if (voiceUrl) {
    transcribedText = await transcribeAudioViaWebSpeech(voiceUrl, targetLang);
  }

  if (!transcribedText) {
    return { transcription: null, translation: null, error: "Could not transcribe audio. Please re-record and speak clearly." };
  }

  let translatedText = null;
  if (needsTranslation) {
    const result = await translateWithClaude(transcribedText, targetLang);
    if (result && result.trim() && result.trim().toLowerCase() !== transcribedText.toLowerCase()) {
      translatedText = result.trim();
    }
  }

  return { transcription: transcribedText, translation: translatedText, targetLabel };
}

/* ─── WEB SPEECH API LIVE TRANSCRIPTION ─── */
function createLiveRecognition() {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return null;
  const recognition = new SpeechRecognition();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = ""; // auto-detect — set via setRecognitionLang() before starting
  return recognition;
}

/* ─── STORAGE ─── */
const LS = {
  get: (k, d) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

// Call recordings are full base64 video/audio blobs (often multiple MB each)
// and localStorage's ~5-10MB total quota is shared with every other chat
// message, photo, and voice note the app stores. Once that quota fills up,
// localStorage.setItem() throws — and LS.set() above silently swallows that
// error — so recordings would still play right after the call (they're
// still sitting in React state / memory) but were never actually written to
// disk, and vanish next time the app loads. IndexedDB has a far larger quota
// and is built for exactly this kind of binary blob storage, so call
// recordings are persisted there instead.
const CALLREC_DB_NAME = "mc3_callrecs_db";
const CALLREC_STORE = "recordings";
function openCallRecDB() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) { reject(new Error("IndexedDB unavailable")); return; }
    const req = indexedDB.open(CALLREC_DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(CALLREC_STORE)) {
        req.result.createObjectStore(CALLREC_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGetAllRecordings() {
  try {
    const db = await openCallRecDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(CALLREC_STORE, "readonly");
      const req = tx.objectStore(CALLREC_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (e) { console.warn("Call recordings: failed to load from IndexedDB", e); return []; }
}
async function idbSaveRecording(rec) {
  try {
    const db = await openCallRecDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(CALLREC_STORE, "readwrite");
      tx.objectStore(CALLREC_STORE).put(rec);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) { console.warn("Call recordings: failed to save to IndexedDB", e); }
}

/* ─── HELPERS ─── */
const ts  = () => new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2,8);
// Per-account localStorage keys, so two people using the same browser never see each other's local data.
const lsKey = (userId, k) => `mc3u_${userId}_${k}`;
const dayKey = (t) => { const d = new Date(t); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
function dayLabel(t) {
  const d = new Date(t), now = new Date(), y = new Date(); y.setDate(now.getDate()-1);
  if (dayKey(t)===dayKey(now)) return "Today";
  if (dayKey(t)===dayKey(y)) return "Yesterday";
  return d.toLocaleDateString([], { day:"numeric", month:"short", year:"numeric" });
}
const pairKey = (a, b) => [a, b].sort().join("|");
const chatUrl = (p) => p.isGroup ? `/api/groups/${encodeURIComponent(p.id)}/messages` : `/api/messages/${encodeURIComponent(p.id)}`;
const fmtFileSize = (b) => b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;
const omit = (obj, keys) => { const o = { ...obj }; keys.forEach(k => delete o[k]); return o; };
function abbr(name) {
  return (name||"?").trim().split(/\s+/).map(w=>w[0]).join("").slice(0,2).toUpperCase();
}

// Read an image file, downscale it to a small square, and return a compact
// JPEG data-URL — used for profile photos so they stay light in
// localStorage and quick to send over the "join" socket event.
function readAndResizeImage(file, maxSize = 240) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not read image"));
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2, sy = (img.height - side) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = maxSize; canvas.height = maxSize;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, side, side, 0, 0, maxSize, maxSize);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const EMOJIS = ["😊","😂","❤️","👍","🙏","😢","😮","🔥","✅","❌","⚠️","🎉","🎈","📚","🎵","🎮","💻","📷","✈️","📞","📹","🔒","📎","🌟","⭐","👏","🙌","💯","✨"];

/* ─── GLOBAL CSS ─── */
const globalCSS = `
  @keyframes ring1  { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(1.18);opacity:.15} }
  @keyframes ring2  { 0%,100%{transform:scale(1);opacity:.3} 50%{transform:scale(1.25);opacity:.05} }
  @keyframes pulse  { 0%,100%{opacity:.6} 50%{opacity:1} }
  @keyframes slideUp{ from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }
  @keyframes slideIn{ from{transform:translateX(-12px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes recPulse{ 0%,100%{opacity:1} 50%{opacity:.25} }
  @keyframes lockPulse{ 0%,100%{transform:scale(1)} 50%{transform:scale(1.07)} }
  @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  * { box-sizing:border-box; margin:0; padding:0; }
  html,body,#root { height:100%; }
  body { font-family:'Plus Jakarta Sans',sans-serif; background:#F1F5F9; }
  ::-webkit-scrollbar { width:4px; height:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:${T.slate300}; border-radius:4px; }
  ::-webkit-scrollbar-thumb:hover { background:${T.slate400}; }
  .bubble-wrap:hover .bubble-actions { opacity:1 !important; }
  .sidebar-item { transition:all .15s ease; }
  .sidebar-item:hover { background:${T.slate100} !important; }
  .ctx-menu { animation:fadeIn .1s ease; }
  .glass { backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px); }
  input,textarea,select { font-family:'Plus Jakarta Sans',sans-serif; }
  button { font-family:'Plus Jakarta Sans',sans-serif; cursor:pointer; }
  @media (max-width:768px) {
    .sidebar-desktop { display:none !important; }
    .sidebar-mobile-visible { display:flex !important; }
    .chat-area { width:100% !important; }
    .msg-bubble { max-width:80% !important; }
  }
  @media (min-width:769px) {
    .sidebar-mobile-visible { display:flex !important; }
    .mobile-back-btn { display:none !important; }
  }
`;
const styleEl = document.createElement("style");
styleEl.textContent = globalCSS;
document.head.appendChild(styleEl);

/* ─── WEBRTC ─── */
let peerConnection = null;
let localStream = null;
const rtcConfig = {
  iceServers: [
    { urls:"stun:stun.l.google.com:19302" },
    { urls:"stun:stun1.l.google.com:19302" },
    // TURN servers — required so audio/video can connect when the two
    // people are on different networks/routers (STUN alone often fails
    // here, which is why calls "connect" but have no audio across networks).
    { urls:"turn:openrelay.metered.ca:80", username:"openrelayproject", credential:"openrelayproject" },
    { urls:"turn:openrelay.metered.ca:443", username:"openrelayproject", credential:"openrelayproject" },
    { urls:"turn:openrelay.metered.ca:443?transport=tcp", username:"openrelayproject", credential:"openrelayproject" },
  ],
};

async function startWebRTCCall(withVideo, onLocal, onRemote, onError, peerId, isCaller) {
  try {
    stopWebRTCCall();
    // Create the peer connection FIRST, synchronously — before awaiting
    // getUserMedia. Previously peerConnection was created only after the
    // camera/mic permission prompt resolved, so if that took even a
    // moment, an incoming offer/ICE candidate from the other side could
    // arrive and be silently dropped (peerConnection was still null),
    // leaving local video/audio working but remote never connecting.
    peerConnection = new RTCPeerConnection(rtcConfig);
    peerConnection.ontrack = (event) => { const rs = event.streams[0]; if (rs) onRemote(rs); };
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && peerId) socket.emit("ice-candidate", { to: peerId, candidate: event.candidate });
    };
    if (!navigator.mediaDevices?.getUserMedia) throw new Error("Browser does not support camera/mic.");
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation:true, noiseSuppression:true, autoGainControl:true },
      video: withVideo ? { width:640, height:480, frameRate:24 } : false,
    });
    onLocal(localStream);
    localStream.getTracks().forEach(track => peerConnection.addTrack(track, localStream));
    if (isCaller) {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      socket.emit("webrtc-offer", { to: peerId, offer });
    }
  } catch (err) { onError(err.message || "Failed to access camera/microphone"); }
}
function stopWebRTCCall() {
  if (localStream)      { localStream.getTracks().forEach(t => t.stop()); localStream = null; }
  if (peerConnection)   { peerConnection.close(); peerConnection = null; }
}
function toggleMicLive(muted) { localStream?.getAudioTracks().forEach(t => { t.enabled = !muted; }); }
function toggleCamLive(off)   { localStream?.getVideoTracks().forEach(t => { t.enabled = !off; }); }

/* ══════════════════════════════════════════════════════════
   TRANSLATION BAR
══════════════════════════════════════════════════════════ */
function TranslationBar({ targetLang, onChangeLang, enabled, onToggle }) {
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:10, padding:"5px 16px",
      background: enabled ? "#F0FDFA" : T.slate50,
      borderBottom: `1px solid ${enabled ? "#A7F3D0" : T.slate200}`,
      flexShrink:0, transition:"background .2s",
    }}>
      <button onClick={onToggle} style={{
        display:"flex", alignItems:"center", gap:6,
        background: enabled ? T.teal : T.slate400,
        border:"none", borderRadius:20, padding:"3px 12px 3px 8px",
        color:"#fff", fontSize:12, fontWeight:700, transition:"background .2s", flexShrink:0,
      }}>
        <span style={{ fontSize:14 }}>🌐</span>
        {enabled ? "Translate ON" : "Translate OFF"}
      </button>
      {enabled && (
        <>
          <span style={{ fontSize:12, color:T.slate500 }}>Translate to:</span>
          <select value={targetLang} onChange={e => onChangeLang(e.target.value)}
            style={{ padding:"3px 8px", borderRadius:8, border:`1.5px solid ${T.teal}`, fontSize:12.5, color:T.slate800, background:T.white, outline:"none", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {LANGUAGES.filter(l => l.code !== "auto").map(l => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <span style={{ fontSize:11, color:T.teal, fontStyle:"italic" }}>Incoming messages auto-translated</span>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   AVATAR
══════════════════════════════════════════════════════════ */
function Avatar({ user, size=40, online, group }) {
  if (group) {
    return (
      <div style={{
        width:size, height:size, borderRadius:size*0.3,
        background:`linear-gradient(135deg,${T.blue500},${T.blue700})`,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:size*0.38, flexShrink:0, overflow:"hidden",
        boxShadow:`0 2px 8px rgba(37,99,235,0.25)`,
      }}>👥</div>
    );
  }
  const rc = ROLE_CONFIG[user?.role] || ROLE_CONFIG.Other;
  // If this user has a real profile photo (set by themselves and synced
  // live via the "join"/"online-users" socket events, or saved locally),
  // show that instead of the initials placeholder.
  const hasPhoto = !!user?.photo;
  return (
    <div style={{ position:"relative", flexShrink:0 }}>
      <div style={{
        width:size, height:size, borderRadius:size*0.3,
        background:hasPhoto?T.slate100:rc.bg, border:`1.5px solid ${rc.color}22`,
        display:"flex", alignItems:"center", justifyContent:"center",
        color:rc.color, fontWeight:700, fontSize:size*0.36,
        fontFamily:"'Plus Jakarta Sans',sans-serif", letterSpacing:0.5,
        overflow:"hidden",
      }}>
        {hasPhoto
          ? <img src={user.photo} alt={user?.name||""} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          : abbr(user?.name)}
      </div>
      {online !== undefined && (
        <div style={{
          position:"absolute", bottom:-1, right:-1,
          width:size*0.28, height:size*0.28, borderRadius:"50%",
          background: online ? T.success : T.slate300,
          border:`2px solid ${T.white}`,
        }} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   RINGTONE — synthesized phone-ring sound (Web Audio API, no
   external file/network needed). Plays a classic "brrring…brrring"
   two-tone pattern on a loop for as long as `active` is true, and
   stops cleanly the moment it flips false or the component unmounts.
══════════════════════════════════════════════════════════ */
function useRingtone(active) {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    ctxRef.current = ctx;
    let stopped = false;

    const ringBurst = () => {
      if (stopped) return;
      // Two overlapping tones (like a classic phone ring), two short
      // pulses ("brr…brr"), then silence before the pattern repeats.
      [0, 0.45].forEach(offset => {
        [440, 480].forEach(freq => {
          const osc  = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = freq;
          const t0 = ctx.currentTime + offset;
          gain.gain.setValueAtTime(0.0001, t0);
          gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.4);
        });
      });
    };

    ringBurst();
    timerRef.current = setInterval(ringBurst, 2200);
    return () => {
      stopped = true;
      clearInterval(timerRef.current);
      ctx.close().catch(()=>{});
    };
  }, [active]);
}

/* ══════════════════════════════════════════════════════════
   CALL RECORDER — automatically records every voice/video call.
   Mixes every participant's audio into one track (Web Audio API)
   and, for video calls, composites every participant's video tile
   onto an offscreen canvas — all without touching or altering any
   of the visible call UI. Recording starts the moment the call
   becomes active and is finalized the moment it ends, then handed
   back as a base64 data URL ready to store in Call History.
══════════════════════════════════════════════════════════ */
function useCallRecorder({ active, isVideo, getStreams, getMeta, onRecordingReady }) {
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let mr = null, audioCtx = null, rafId = null;
    let hiddenVideos = [];
    let recordedMime = isVideo ? "video/webm" : "audio/webm";
    let finalIsVideo = isVideo;
    const chunks = [];

    let attempts = 0;
    const maxAttempts = isVideo ? 26 : 5; // video: up to ~8s (camera/negotiation is slow); voice: ~1.5s
    let initTimer = null;

    const tryStart = () => {
      if (stopped) return;
      attempts++;
      const { local, remotes = [] } = getStreams() || {};
      const allStreams = [local, ...remotes].filter(Boolean);
      const hasVideoTrack = allStreams.some(s => s.getVideoTracks().length > 0);

      // For video calls, keep waiting for an actual video track to show up
      // (camera permission + negotiation is slower than audio-only, so the
      // streams often aren't ready yet on the first check) rather than
      // silently starting an audio-only recording of a "video" call.
      if (isVideo && !hasVideoTrack && attempts < maxAttempts) {
        initTimer = setTimeout(tryStart, 300);
        return;
      }
      if (allStreams.length === 0) {
        if (attempts < maxAttempts) initTimer = setTimeout(tryStart, 300);
        return;
      }

      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const dest = audioCtx.createMediaStreamDestination();
        allStreams.forEach(s => {
          if (!s.getAudioTracks().length) return;
          try { audioCtx.createMediaStreamSource(s).connect(dest); } catch(e) {}
        });
        let tracks = [...dest.stream.getAudioTracks()];
        let recordedIsVideo = false;

        if (isVideo) {
          const remoteVideoStreams = remotes.filter(s => s && s.getVideoTracks().length);
          const localHasVideo = local && local.getVideoTracks().length > 0;

          if (remoteVideoStreams.length <= 1) {
            // The common case: a 1:1 call (or a group call where only one
            // other person currently has video). Use that single real video
            // track directly — no canvas, no hidden decoding <video>
            // element, nothing that can silently fail. Prefer the remote
            // party's video (that's who you're talking to); fall back to
            // your own if the remote has none yet.
            const source = remoteVideoStreams[0] || (localHasVideo ? local : null);
            const track = source?.getVideoTracks()[0];
            if (track && track.readyState === "live") {
              tracks = [track, ...tracks];
              recordedIsVideo = true;
            }
          } else {
            // Multiple remote video tiles (real multi-party group call) —
            // these genuinely need to be combined into one frame, which
            // does require compositing them onto a canvas via hidden
            // decoding <video> elements.
            const videoStreams = remoteVideoStreams;
            hiddenVideos = videoStreams.map(s => {
              const v = document.createElement("video");
              v.srcObject = s; v.muted = true; v.playsInline = true;
              // Must be attached to the page (even if invisible) for the
              // browser to reliably decode frames — a detached <video>
              // often never actually plays.
              v.style.position = "fixed";
              v.style.left = "-9999px";
              v.style.width = "2px";
              v.style.height = "2px";
              document.body.appendChild(v);
              v.play().catch(()=>{});
              return v;
            });
            if (hiddenVideos.length) {
              recordedIsVideo = true;
              const cols = 2;
              const rows = Math.max(1, Math.ceil(hiddenVideos.length / cols));
              const canvas = document.createElement("canvas");
              canvas.width = 480*cols; canvas.height = 360*rows;
              const ctx = canvas.getContext("2d");
              const draw = () => {
                if (stopped) return;
                ctx.fillStyle = "#0f172a"; ctx.fillRect(0,0,canvas.width,canvas.height);
                hiddenVideos.forEach((v,i) => {
                  const x=(i%cols)*480, y=Math.floor(i/cols)*360;
                  try { ctx.drawImage(v,x,y,480,360); } catch(e) {}
                });
                rafId = requestAnimationFrame(draw);
              };
              draw();
              tracks = [...canvas.captureStream(15).getVideoTracks(), ...tracks];
            }
          }
        }

        if (tracks.length === 0) return;
        finalIsVideo = recordedIsVideo;
        const combined = new MediaStream(tracks);
        const mimeCandidates = recordedIsVideo
          ? ["video/webm;codecs=vp8,opus","video/webm"]
          : ["audio/webm;codecs=opus","audio/webm"];
        const supported = mimeCandidates.find(m => MediaRecorder.isTypeSupported(m)) || "";
        // IMPORTANT: `supported` (e.g. "video/webm;codecs=vp8,opus") is only
        // used to configure the MediaRecorder itself, which needs the codec
        // details. It must NOT be used as the Blob's `type` — that type gets
        // embedded verbatim into the data: URL we hand to <video>/<audio>,
        // and a data: URL's header/data split happens on the FIRST comma in
        // the whole string. "codecs=vp8,opus" contains a comma, so it would
        // truncate the URL right there and silently corrupt every video
        // recording (plays back as 0:00 / unplayable). Store a plain,
        // comma-free base type instead — playback doesn't need the codecs
        // listed, the browser sniffs them from the actual bytes.
        recordedMime = recordedIsVideo ? "video/webm" : "audio/webm";
        mr = new MediaRecorder(combined, supported ? { mimeType: supported } : {});
        mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
        mr.start(1000);
      } catch(e) {
        // Combined video+audio recorder failed (e.g. unsupported codec combo
        // on this browser) — fall back to at least recording the audio
        // rather than losing the recording entirely.
        console.warn("Call recorder: video setup failed, falling back to audio-only", e);
        hiddenVideos.forEach(v => { try{v.pause();}catch(err){} v.srcObject=null; v.remove(); });
        hiddenVideos = [];
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        try {
          finalIsVideo = false;
          const dest2 = audioCtx.createMediaStreamDestination();
          allStreams.forEach(s => { if (!s.getAudioTracks().length) return; try { audioCtx.createMediaStreamSource(s).connect(dest2); } catch(err) {} });
          recordedMime = "audio/webm";
          mr = new MediaRecorder(dest2.stream, { mimeType: "audio/webm" });
          mr.ondataavailable = ev => { if (ev.data.size > 0) chunks.push(ev.data); };
          mr.start(1000);
        } catch(err2) { console.warn("Call recorder: audio-only fallback also failed", err2); }
      }
    };

    initTimer = setTimeout(tryStart, 300);

    return () => {
      stopped = true;
      clearTimeout(initTimer);
      if (rafId) cancelAnimationFrame(rafId);
      hiddenVideos.forEach(v => { try{v.pause();}catch(e){} v.srcObject=null; v.remove(); });
      const finish = () => {
        if (audioCtx) audioCtx.close().catch(()=>{});
        if (chunks.length === 0) return; // call ended before anything was captured
        const blob = new Blob(chunks, { type: recordedMime });
        const reader = new FileReader();
        reader.onload = ev => {
          onRecordingReady?.({ ...(getMeta ? getMeta() : {}), recordingUrl: ev.target.result, mime: recordedMime, isVideo: finalIsVideo });
        };
        reader.readAsDataURL(blob);
      };
      if (mr && mr.state !== "inactive") { mr.onstop = finish; mr.stop(); }
      else finish();
    };
  }, [active]);
}

/* ══════════════════════════════════════════════════════════
   CALL OVERLAY
══════════════════════════════════════════════════════════ */
function CallOverlay({ callState, callerUser, calleeUser, meId, onAccept, onReject, onEnd, onForceEnd, onRecordingReady }) {
  const [dur, setDur]       = useState(0);
  const [muted, setMuted]   = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [err, setErr]       = useState("");
  const localVidRef  = useRef(null);
  const remoteVidRef = useRef(null);
  const remoteAudRef = useRef(null);
  const timerRef     = useRef(null);
  const durRef       = useRef(0);
  const isVideo   = callState.type === "video";
  const isActive  = callState.phase === "active";
  const isIncoming = callState.phase === "ringing" && callState.callerId !== meId;
  const isOutgoing = callState.phase === "ringing" && callState.callerId === meId;
  useRingtone(isIncoming);
  const peerUser = meId === callerUser?.id ? calleeUser : callerUser;
  const peerId   = meId === callState.callerId ? callState.calleeId : callState.callerId;
  const isCaller = meId === callState.callerId;
  const remoteStreamRef = useRef(null);
  const everActiveRef = useRef(false);
  if (isActive) everActiveRef.current = true;
  const fmt = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  useEffect(() => { durRef.current = dur; }, [dur]);

  useCallRecorder({
    active: everActiveRef.current,
    isVideo,
    getStreams: () => ({ local: localStream, remotes: [remoteStreamRef.current] }),
    getMeta: () => ({ peerId: peerUser?.id, peerName: peerUser?.name||peerId, callType: callState.type, isGroup:false }),
    onRecordingReady,
  });

  const attachRemote = (rs) => {
    remoteStreamRef.current = rs;
    if (remoteAudRef.current) { remoteAudRef.current.srcObject = rs; remoteAudRef.current.play().catch(()=>{}); }
    if (isVideo && remoteVidRef.current) { remoteVidRef.current.srcObject = rs; setTimeout(()=>remoteVidRef.current?.play().catch(()=>{}), 100); }
  };

  // Register WebRTC signalling listeners as soon as the overlay is visible
  // (not gated on isActive) so the callee doesn't miss the offer that arrives
  // right after call-accepted is emitted.
  useEffect(() => {
    if (!callState.visible) return;
    // Candidates can arrive over the socket before setRemoteDescription()
    // has finished processing the offer/answer. Previously these were
    // silently dropped (addIceCandidate throws, caught and ignored) —
    // now we queue them and flush once the remote description is set.
    let candidateQueue = [];
    const flushQueue = async () => {
      const queued = candidateQueue; candidateQueue = [];
      for (const c of queued) { try { await peerConnection.addIceCandidate(new RTCIceCandidate(c)); } catch(e) {} }
    };
    const onOffer = async (data) => {
      if (!peerConnection) return;
      try {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(data.offer));
        await flushQueue();
        const a = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(a);
        socket.emit("webrtc-answer", { to: peerId, answer: a });
      } catch(e) { console.warn("webrtc-offer handler error:", e); }
    };
    const onAnswer = async (data) => {
      if (!peerConnection) return;
      try { await peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer)); await flushQueue(); } catch(e) {}
    };
    const onIce = async (data) => {
      if (!data.candidate) return;
      if (!peerConnection || !peerConnection.remoteDescription) { candidateQueue.push(data.candidate); return; }
      try { await peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch(e) {}
    };
    // The other side hung up, or disconnected entirely (server detects
    // this and relays it too) — close our side immediately instead of
    // being left "connected" forever with a running timer (sync bug fix).
    const onCallEnded = () => { onForceEnd?.(fmt(durRef.current)); };
    socket.on("webrtc-offer", onOffer);
    socket.on("webrtc-answer", onAnswer);
    socket.on("ice-candidate", onIce);
    socket.on("call-ended", onCallEnded);
    return () => {
      socket.off("webrtc-offer", onOffer);
      socket.off("webrtc-answer", onAnswer);
      socket.off("ice-candidate", onIce);
      socket.off("call-ended", onCallEnded);
    };
  }, [callState.visible, peerId]);

  useEffect(() => {
    if (!isActive) return;
    setDur(0); setErr("");
    startWebRTCCall(isVideo,
      (ls) => {
        if (isVideo && localVidRef.current) { localVidRef.current.srcObject = ls; setTimeout(()=>localVidRef.current?.play().catch(()=>{}),100); }
        timerRef.current = setInterval(() => setDur(d=>d+1), 1000);
      },
      attachRemote, (e)=>setErr(e), peerId, isCaller
    );
    return () => { clearInterval(timerRef.current); stopWebRTCCall(); };
  }, [isActive]);

  if (!callState.visible) return null;

  if (isVideo && isActive) return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:T.slate900, display:"flex", flexDirection:"column" }}>
      <audio ref={remoteAudRef} autoPlay playsInline style={{ display:"none" }} />
      <video ref={remoteVidRef} autoPlay playsInline style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
      <div style={{ position:"absolute", top:0, left:0, right:0, padding:"20px 24px", background:"linear-gradient(to bottom,rgba(15,23,42,.7),transparent)" }}>
        <div style={{ color:"#fff", fontSize:18, fontWeight:700 }}>{peerUser?.name}</div>
        <div style={{ color:"rgba(255,255,255,.7)", fontSize:13, marginTop:3 }}>{peerUser?.role} · {fmt(dur)}</div>
        {err && <div style={{ color:T.danger, fontSize:12, marginTop:4, background:T.dangerBg, borderRadius:6, padding:"4px 8px", display:"inline-block" }}>{err}</div>}
      </div>
      <video ref={localVidRef} autoPlay playsInline muted style={{ position:"absolute", bottom:100, right:16, width:120, height:88, objectFit:"cover", borderRadius:12, border:"2px solid rgba(255,255,255,.3)" }} />
      <div style={{ position:"absolute", bottom:30, left:0, right:0, display:"flex", justifyContent:"center", gap:16 }}>
        <CallBtn icon={muted?"🔇":"🎤"} label={muted?"Unmute":"Mute"} dark={muted} onClick={()=>{ const m=!muted; setMuted(m); toggleMicLive(m); }} />
        <CallBtn icon="📵" label="End" danger onClick={()=>{ clearInterval(timerRef.current); onEnd(fmt(dur)); }} large />
        <CallBtn icon={camOff?"📷":"📹"} label={camOff?"Show":"Hide"} dark={camOff} onClick={()=>{ const c=!camOff; setCamOff(c); toggleCamLive(c); }} />
      </div>
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:T.slate900, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
      <audio ref={remoteAudRef} autoPlay playsInline style={{ display:"none" }} />
      <div style={{ background:"rgba(255,255,255,.06)", backdropFilter:"blur(20px)", borderRadius:32, padding:"48px 40px", textAlign:"center", border:"1px solid rgba(255,255,255,.1)", minWidth:300, maxWidth:380 }}>
        <div style={{ marginBottom:24, position:"relative", display:"inline-block" }}>
          {(isIncoming||isOutgoing) && <>
            <div style={{ position:"absolute", inset:-20, borderRadius:"50%", border:"2px solid rgba(255,255,255,.15)", animation:"ring1 2s infinite" }} />
            <div style={{ position:"absolute", inset:-40, borderRadius:"50%", border:"2px solid rgba(255,255,255,.08)", animation:"ring2 2s infinite .4s" }} />
          </>}
          <Avatar user={peerUser} size={88} />
        </div>
        <div style={{ color:"#fff", fontSize:24, fontWeight:700, marginBottom:4, fontFamily:"'Instrument Serif',serif" }}>{peerUser?.name}</div>
        <div style={{ color:"rgba(255,255,255,.5)", fontSize:13, marginBottom:12 }}>{peerUser?.role}</div>
        {isIncoming && <div style={{ color:T.blue400, fontSize:14, marginBottom:4 }}>Incoming {isVideo?"📹 Video":"📞 Voice"} Call</div>}
        {isOutgoing && <div style={{ color:"rgba(255,255,255,.5)", fontSize:14, animation:"pulse 1.4s infinite" }}>Calling…</div>}
        {isActive && <>
          <div style={{ color:T.success, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}><span style={{ width:8, height:8, borderRadius:"50%", background:T.success, display:"inline-block" }} />Connected</div>
          <div style={{ color:"#fff", fontSize:28, fontWeight:300, marginTop:4, fontVariantNumeric:"tabular-nums" }}>{fmt(dur)}</div>
        </>}
        {err && <div style={{ color:T.danger, fontSize:12, background:T.dangerBg, borderRadius:8, padding:"6px 12px", margin:"8px 0", display:"inline-block" }}>{err}</div>}
        <div style={{ display:"flex", gap:20, marginTop:32, justifyContent:"center" }}>
          {isIncoming && <>
            <RingBtn icon="📞" label="Accept" bg={T.success} onClick={onAccept} />
            <RingBtn icon="📵" label="Decline" bg={T.danger} onClick={onReject} />
          </>}
          {isOutgoing && <RingBtn icon="📵" label="Cancel" bg={T.danger} onClick={()=>{ clearInterval(timerRef.current); onEnd(""); }} />}
          {isActive && <>
            <RingBtn icon={muted?"🔇":"🎤"} label={muted?"Unmute":"Mute"} bg={muted?T.danger:"rgba(255,255,255,.15)"} onClick={()=>{ const m=!muted; setMuted(m); toggleMicLive(m); }} />
            <RingBtn icon="📵" label="End" bg={T.danger} onClick={()=>{ clearInterval(timerRef.current); onEnd(fmt(dur)); }} />
          </>}
        </div>
      </div>
    </div>
  );
}

function CallBtn({ icon, label, dark, danger, large, onClick }) {
  return (
    <div onClick={onClick} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"pointer" }}>
      <div style={{ width:large?60:50, height:large?60:50, borderRadius:"50%", background:danger?T.danger:dark?"rgba(255,255,255,.2)":"rgba(255,255,255,.12)", border:"1px solid rgba(255,255,255,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:large?22:18 }}>{icon}</div>
      <span style={{ color:"rgba(255,255,255,.6)", fontSize:11 }}>{label}</span>
    </div>
  );
}
function RingBtn({ icon, label, bg, onClick }) {
  return (
    <div onClick={onClick} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, cursor:"pointer" }}>
      <div style={{ width:60, height:60, borderRadius:"50%", background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>{icon}</div>
      <span style={{ color:"rgba(255,255,255,.5)", fontSize:12 }}>{label}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   GROUP CALL OVERLAY — mesh WebRTC (each participant connects
   directly to every other participant), with ring/accept/reject/
   ignore, join-call-in-progress, and full teardown on leave/end.
══════════════════════════════════════════════════════════ */
function ParticipantAudioTile({ name, muted }) {
  return (
    <div style={{ borderRadius:14, background:"#1e293b", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, gap:10, aspectRatio:"4/3" }}>
      <div style={{ width:56, height:56, borderRadius:"50%", background:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:20 }}>{(name||"?").slice(0,1).toUpperCase()}</div>
      <div style={{ color:"#fff", fontSize:13, fontWeight:600 }}>{name}{muted?" 🔇":""}</div>
    </div>
  );
}
function GroupRemoteVideoTile({ stream, name }) {
  const vidRef = useRef(null);
  useEffect(() => { if (vidRef.current) { vidRef.current.srcObject = stream; vidRef.current.play().catch(()=>{}); } }, [stream]);
  return (
    <div style={{ position:"relative", borderRadius:14, overflow:"hidden", background:"#0f172a", aspectRatio:"4/3" }}>
      <video ref={vidRef} autoPlay playsInline style={{ width:"100%", height:"100%", objectFit:"cover" }} />
      <div style={{ position:"absolute", bottom:6, left:8, color:"#fff", fontSize:12, fontWeight:600, textShadow:"0 1px 3px rgba(0,0,0,.6)" }}>{name}</div>
    </div>
  );
}

function GroupCallOverlay({ callState, me, contacts, groups, onAccept, onReject, onIgnore, onEnd, onForceEnd, onRecordingReady }) {
  const [dur, setDur]             = useState(0);
  const [muted, setMuted]         = useState(false);
  const [camOff, setCamOff]       = useState(false);
  const [err, setErr]             = useState("");
  const [remoteStreams, setRemoteStreams] = useState({}); // userId -> MediaStream
  const remoteStreamsRef = useRef({});
  useEffect(() => { remoteStreamsRef.current = remoteStreams; }, [remoteStreams]);
  const localVidRef   = useRef(null);
  const timerRef       = useRef(null);
  const durRef          = useRef(0);
  const pcsRef           = useRef({});   // userId -> RTCPeerConnection
  const candidateQRef    = useRef({});   // userId -> queued ICE candidates
  const localStreamRef   = useRef(null);

  const isVideo   = callState.type === "video";
  const isActive  = callState.phase === "active";
  const isRinging = callState.phase === "ringing";
  useRingtone(isRinging && callState.callerId !== me.id);
  const group   = groups.find(g => g.id === callState.groupId);
  const starter = callState.callerId === me.id ? me : contacts.find(c => c.id === callState.callerId);
  const fmt = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const getMemberName = (id) => id===me.id ? me.name : (contacts.find(c=>c.id===id)?.name || id);
  const everActiveRef = useRef(false);
  if (isActive) everActiveRef.current = true;
  const everHadOtherParticipantRef = useRef(false);

  useEffect(() => { durRef.current = dur; }, [dur]);

  // If everyone else in the call has left, don't keep this side "running"
  // forever waiting for a group-call-ended event the server never sends
  // just because one person hung up — end it for the last remaining person too.
  useEffect(() => {
    if (!isActive) return;
    const otherCount = Object.keys(remoteStreams).length;
    if (otherCount > 0) { everHadOtherParticipantRef.current = true; return; }
    if (everHadOtherParticipantRef.current) onEnd?.(fmt(durRef.current));
  }, [isActive, remoteStreams]);

  useCallRecorder({
    active: everActiveRef.current,
    isVideo,
    getStreams: () => ({ local: localStreamRef.current, remotes: Object.values(remoteStreamsRef.current) }),
    getMeta: () => ({ groupId: callState.groupId, groupName: group?.name, callType: callState.type, isGroup:true,
      participants: [me.id, ...Object.keys(remoteStreamsRef.current)].map(getMemberName) }),
    onRecordingReady,
  });

  const getOrCreatePeer = (userId) => {
    if (pcsRef.current[userId]) return pcsRef.current[userId];
    const pc = new RTCPeerConnection(rtcConfig);
    pcsRef.current[userId] = pc;
    if (!candidateQRef.current[userId]) candidateQRef.current[userId] = [];
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current));
    pc.ontrack = (e) => { const rs = e.streams[0]; if (rs) setRemoteStreams(prev => ({ ...prev, [userId]: rs })); };
    pc.onicecandidate = (e) => { if (e.candidate) socket.emit("group-ice-candidate", { to:userId, from:me.id, candidate:e.candidate }); };
    return pc;
  };
  const closePeer = (userId) => {
    const pc = pcsRef.current[userId];
    if (pc) { pc.close(); delete pcsRef.current[userId]; }
    delete candidateQRef.current[userId];
    setRemoteStreams(prev => { if (!(userId in prev)) return prev; const n={...prev}; delete n[userId]; return n; });
  };
  const closeAllPeers = () => { Object.keys(pcsRef.current).forEach(closePeer); };
  const initiateOfferTo = async (userId) => {
    const pc = getOrCreatePeer(userId);
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit("group-webrtc-offer", { to:userId, from:me.id, offer });
    } catch(e) { console.warn("group offer error", e); }
  };

  // Local media + full mesh signaling — only while the call is active.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    setDur(0); setErr("");

    const flushQueueFor = async (userId) => {
      const pc = pcsRef.current[userId];
      const q = candidateQRef.current[userId] || [];
      candidateQRef.current[userId] = [];
      for (const c of q) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch(e) {} }
    };
    const onParticipants = (data) => {
      if (data.groupId !== callState.groupId) return;
      (data.participants||[]).forEach(uid => initiateOfferTo(uid));
    };
    const onOffer = async (data) => {
      if (data.to !== me.id) return;
      const pc = getOrCreatePeer(data.from);
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        await flushQueueFor(data.from);
        const a = await pc.createAnswer();
        await pc.setLocalDescription(a);
        socket.emit("group-webrtc-answer", { to:data.from, from:me.id, answer:a });
      } catch(e) { console.warn("group answer error", e); }
    };
    const onAnswer = async (data) => {
      if (data.to !== me.id) return;
      const pc = pcsRef.current[data.from];
      if (!pc) return;
      try { await pc.setRemoteDescription(new RTCSessionDescription(data.answer)); await flushQueueFor(data.from); } catch(e) {}
    };
    const onIceCand = async (data) => {
      if (data.to !== me.id || !data.candidate) return;
      const pc = pcsRef.current[data.from];
      if (!pc || !pc.remoteDescription) { candidateQRef.current[data.from] = [...(candidateQRef.current[data.from]||[]), data.candidate]; return; }
      try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch(e) {}
    };
    const onUserLeft = (data) => { if (data.groupId===callState.groupId) closePeer(data.userId); };
    const onGroupCallEnded = (data) => { if (data.groupId===callState.groupId) onForceEnd?.(fmt(durRef.current)); };

    socket.on("group-call-participants", onParticipants);
    socket.on("group-webrtc-offer", onOffer);
    socket.on("group-webrtc-answer", onAnswer);
    socket.on("group-ice-candidate", onIceCand);
    socket.on("group-call-user-left", onUserLeft);
    socket.on("group-call-ended", onGroupCallEnded);

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation:true, noiseSuppression:true, autoGainControl:true },
          video: isVideo ? { width:640, height:480, frameRate:24 } : false,
        });
        if (cancelled) { stream.getTracks().forEach(t=>t.stop()); return; }
        localStreamRef.current = stream;
        if (isVideo && localVidRef.current) { localVidRef.current.srcObject = stream; setTimeout(()=>localVidRef.current?.play().catch(()=>{}),100); }
        // Attach to any peer connections that were created before media was ready
        Object.values(pcsRef.current).forEach(pc => stream.getTracks().forEach(t => {
          if (!pc.getSenders().find(s=>s.track===t)) pc.addTrack(t, stream);
        }));
        timerRef.current = setInterval(()=>setDur(d=>d+1),1000);
        // Non-starters: ask the server who's already in the call so we can mesh-connect.
        if (callState.callerId !== me.id) socket.emit("group-call-join", { groupId: callState.groupId, userId: me.id });
      } catch(e) { if (!cancelled) setErr(e.message || "Failed to access camera/microphone"); }
    })();

    return () => {
      cancelled = true;
      socket.off("group-call-participants", onParticipants);
      socket.off("group-webrtc-offer", onOffer);
      socket.off("group-webrtc-answer", onAnswer);
      socket.off("group-ice-candidate", onIceCand);
      socket.off("group-call-user-left", onUserLeft);
      socket.off("group-call-ended", onGroupCallEnded);
      clearInterval(timerRef.current);
      closeAllPeers();
      if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t=>t.stop()); localStreamRef.current=null; }
    };
  }, [isActive, callState.groupId]);

  // While just ringing, also close the ring if the whole call session ends
  // before we respond (e.g. everyone else already hung up).
  useEffect(() => {
    if (!isRinging) return;
    const onGroupCallEnded = (data) => { if (data.groupId===callState.groupId) onForceEnd?.(""); };
    socket.on("group-call-ended", onGroupCallEnded);
    return () => socket.off("group-call-ended", onGroupCallEnded);
  }, [isRinging, callState.groupId]);

  if (!callState.visible) return null;

  const toggleMute = () => { const m=!muted; setMuted(m); localStreamRef.current?.getAudioTracks().forEach(t=>t.enabled=!m); };
  const toggleCam  = () => { const c=!camOff; setCamOff(c); localStreamRef.current?.getVideoTracks().forEach(t=>t.enabled=!c); };

  if (isRinging) return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:T.slate900, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"rgba(255,255,255,.06)", backdropFilter:"blur(20px)", borderRadius:32, padding:"48px 40px", textAlign:"center", border:"1px solid rgba(255,255,255,.1)", minWidth:300, maxWidth:380 }}>
        <div style={{ marginBottom:24, position:"relative", display:"inline-block" }}>
          <div style={{ position:"absolute", inset:-20, borderRadius:"50%", border:"2px solid rgba(255,255,255,.15)", animation:"ring1 2s infinite" }} />
          <div style={{ position:"absolute", inset:-40, borderRadius:"50%", border:"2px solid rgba(255,255,255,.08)", animation:"ring2 2s infinite .4s" }} />
          <Avatar group size={88} />
        </div>
        <div style={{ color:"#fff", fontSize:24, fontWeight:700, marginBottom:4, fontFamily:"'Instrument Serif',serif" }}>{group?.name || "Group"}</div>
        <div style={{ color:"rgba(255,255,255,.5)", fontSize:13, marginBottom:12 }}>{starter?.name || "Someone"} started a call</div>
        <div style={{ color:T.blue400, fontSize:14, marginBottom:4 }}>Incoming Group {isVideo?"📹 Video":"📞 Voice"} Call</div>
        <div style={{ display:"flex", gap:16, marginTop:32, justifyContent:"center", flexWrap:"wrap" }}>
          <RingBtn icon="📞" label="Accept" bg={T.success} onClick={onAccept} />
          <RingBtn icon="📵" label="Reject" bg={T.danger} onClick={onReject} />
          <RingBtn icon="🙈" label="Ignore" bg="rgba(255,255,255,.15)" onClick={onIgnore} />
        </div>
      </div>
    </div>
  );

  const remoteIds = Object.keys(remoteStreams);
  const tileCount = remoteIds.length + 1;
  const cols = tileCount<=1?1:tileCount<=4?2:3;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:T.slate900, display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"14px 20px", flexShrink:0 }}>
        <div style={{ color:"#fff", fontSize:16, fontWeight:700 }}>{group?.name || "Group Call"}</div>
        <div style={{ color:"rgba(255,255,255,.6)", fontSize:12.5, marginTop:2 }}>{tileCount} in call · {fmt(dur)}</div>
        {err && <div style={{ color:T.danger, fontSize:12, marginTop:4, background:T.dangerBg, borderRadius:6, padding:"4px 8px", display:"inline-block" }}>{err}</div>}
      </div>
      <div style={{ flex:1, display:"grid", gridTemplateColumns:`repeat(${cols}, 1fr)`, gap:8, padding:"0 16px 16px", overflow:"auto", alignContent:"start" }}>
        {isVideo ? (
          <div style={{ position:"relative", borderRadius:14, overflow:"hidden", background:"#0f172a", aspectRatio:"4/3" }}>
            <video ref={localVidRef} autoPlay playsInline muted style={{ width:"100%", height:"100%", objectFit:"cover" }} />
            <div style={{ position:"absolute", bottom:6, left:8, color:"#fff", fontSize:12, fontWeight:600, textShadow:"0 1px 3px rgba(0,0,0,.6)" }}>{me.name} (You)</div>
          </div>
        ) : <ParticipantAudioTile name={`${me.name} (You)`} muted={muted} />}
        {remoteIds.map(uid => isVideo
          ? <GroupRemoteVideoTile key={uid} stream={remoteStreams[uid]} name={getMemberName(uid)} />
          : <ParticipantAudioTile key={uid} name={getMemberName(uid)} />
        )}
      </div>
      {/* Audio-only calls: video tiles already carry audio via <video>, but
          plain voice calls need a dedicated (hidden) audio element per peer. */}
      {!isVideo && remoteIds.map(uid => (
        <audio key={"a"+uid} autoPlay playsInline style={{display:"none"}}
          ref={el=>{ if (el && remoteStreams[uid] && el.srcObject!==remoteStreams[uid]) { el.srcObject = remoteStreams[uid]; el.play().catch(()=>{}); } }} />
      ))}
      <div style={{ padding:"18px 0 30px", display:"flex", justifyContent:"center", gap:16, flexShrink:0 }}>
        <CallBtn icon={muted?"🔇":"🎤"} label={muted?"Unmute":"Mute"} dark={muted} onClick={toggleMute} />
        <CallBtn icon="📵" label="Leave" danger onClick={()=>onEnd(fmt(dur))} large />
        {isVideo && <CallBtn icon={camOff?"📷":"📹"} label={camOff?"Show":"Hide"} dark={camOff} onClick={toggleCam} />}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   CAMERA CAPTURE
══════════════════════════════════════════════════════════ */
function CameraCapture({ onSend, onClose }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [err, setErr]     = useState("");
  const [ready, setReady] = useState(false);

  const startCamera = async () => {
    try {
      setErr(""); setReady(false);
      const stream = await navigator.mediaDevices.getUserMedia({ video:{ width:640, height:480, facingMode:"user" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(()=>setReady(true)).catch(e=>{ if(e.name!=="AbortError") setErr("Could not start camera preview."); });
        };
      }
    } catch { setErr("Camera access denied or unavailable."); }
  };
  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t=>t.stop()); streamRef.current=null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  };
  useEffect(() => { startCamera(); return ()=>stopCamera(); }, []);

  const capture = () => {
    const canvas=canvasRef.current, video=videoRef.current;
    if (!canvas||!video) return;
    canvas.width=video.videoWidth||640; canvas.height=video.videoHeight||480;
    canvas.getContext("2d").drawImage(video,0,0);
    setCaptured(canvas.toDataURL("image/jpeg",0.92)); stopCamera();
  };
  const retake = () => { setCaptured(null); startCamera(); };
  const send   = () => { onSend({ type:"image", imageUrl:captured, caption:"" }); onClose(); };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9500, background:"rgba(15,23,42,0.92)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:20, padding:20 }}>
      <canvas ref={canvasRef} style={{ display:"none" }} />
      <div style={{ color:"#fff", fontSize:18, fontWeight:600 }}>📷 Camera Capture</div>
      {err && <div style={{ background:T.dangerBg, color:T.danger, borderRadius:10, padding:"10px 16px", fontSize:13 }}>{err}</div>}
      {!captured ? (
        <>
          <div style={{ position:"relative", borderRadius:16, overflow:"hidden", border:`2px solid ${T.blue500}`, maxWidth:"90vw" }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ display:"block", maxWidth:"80vw", maxHeight:"55vh", borderRadius:14 }} />
            {!ready && !err && (
              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(15,23,42,.6)", color:"#fff", fontSize:14, borderRadius:14, flexDirection:"column", gap:10 }}>
                <div style={{ width:28, height:28, border:`3px solid ${T.blue400}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .8s linear infinite" }} />
                Initialising camera…
              </div>
            )}
          </div>
          <div style={{ display:"flex", gap:16 }}>
            <button onClick={()=>{ stopCamera(); onClose(); }} style={{ padding:"12px 24px", borderRadius:10, border:`1.5px solid rgba(255,255,255,.2)`, background:"rgba(255,255,255,.08)", color:"#fff", fontWeight:600, fontSize:14 }}>Cancel</button>
            <button onClick={capture} disabled={!ready} style={{ padding:"12px 28px", borderRadius:10, border:"none", background:ready?T.blue600:T.slate600, color:"#fff", fontWeight:700, fontSize:14 }}>📷 Capture</button>
          </div>
        </>
      ) : (
        <>
          <img src={captured} alt="Preview" style={{ borderRadius:16, border:`2px solid ${T.blue500}`, maxWidth:"80vw", maxHeight:"55vh", display:"block" }} />
          <div style={{ display:"flex", gap:16 }}>
            <button onClick={retake} style={{ padding:"12px 24px", borderRadius:10, border:`1.5px solid rgba(255,255,255,.2)`, background:"rgba(255,255,255,.08)", color:"#fff", fontWeight:600, fontSize:14 }}>🔄 Retake</button>
            <button onClick={send} style={{ padding:"12px 28px", borderRadius:10, border:"none", background:T.success, color:"#fff", fontWeight:700, fontSize:14 }}>✅ Send</button>
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   VOICE RECORDER
══════════════════════════════════════════════════════════ */
function VoiceRecorder({ onSend, onCancel, targetLang, translateEnabled }) {
  const [seconds, setSeconds]           = useState(0);
  const [liveText, setLiveText]         = useState("");
  const mrRef         = useRef(null);
  const timerRef      = useRef(null);
  const recognitionRef = useRef(null);
  const chunksRef     = useRef([]);
  const mimeTypeRef   = useRef("audio/webm");
  const transcriptRef = useRef("");

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      const mimes = ["audio/webm;codecs=opus","audio/webm","audio/ogg;codecs=opus","audio/mp4"];
      const supported = mimes.find(m => MediaRecorder.isTypeSupported(m)) || "";
      mimeTypeRef.current = supported || "audio/webm";

      const mr = new MediaRecorder(stream, supported ? { mimeType: supported } : {});
      mrRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        const reader = new FileReader();
        reader.onload = (ev) => {
          const base64 = ev.target.result.split(",")[1];
          onSend({
            type: "voice",
            voiceUrl: ev.target.result,
            voiceBase64: base64,
            voiceMime: mimeTypeRef.current,
            liveTranscript: transcriptRef.current,
            duration: seconds,
          });
        };
        reader.readAsDataURL(blob);
      };
      mr.start(200);

      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const rec = new SpeechRecognition();
        rec.continuous = true;
        rec.interimResults = true;
        // Set recognition language based on who is speaking
        // When translating TO English → speaker is Indian (Tamil default)
        // When translating TO Tamil/Hindi/Telugu → speaker could be any language
        rec.lang = (targetLang === "en") ? "ta-IN"
                 : (targetLang === "ta") ? "ta-IN"
                 : (targetLang === "hi") ? "hi-IN"
                 : (targetLang === "te") ? "te-IN"
                 : (targetLang === "kn") ? "kn-IN"
                 : (targetLang === "ml") ? "ml-IN"
                 : "ta-IN";
        recognitionRef.current = rec;

        rec.onresult = (event) => {
          let interim = "";
          let finalPart = "";
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              finalPart += event.results[i][0].transcript + " ";
            } else {
              interim += event.results[i][0].transcript;
            }
          }
          if (finalPart) transcriptRef.current += finalPart;
          setLiveText(transcriptRef.current + interim);
        };
        rec.onerror = () => {};
        try { rec.start(); } catch(e) {}
      }
    }).catch(() => onCancel());

    return () => {
      clearInterval(timerRef.current);
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
      if (mrRef.current?.state === "recording") mrRef.current.stop();
    };
  }, []);

  const stop = () => {
    clearInterval(timerRef.current);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
    if (mrRef.current?.state === "recording") mrRef.current.stop();
  };
  const cancel = () => {
    clearInterval(timerRef.current);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
    if (mrRef.current) {
      mrRef.current.stream?.getTracks().forEach(t => t.stop());
      if (mrRef.current.state === "recording") {
        mrRef.current.ondataavailable = null;
        mrRef.current.onstop = null;
        mrRef.current.stop();
      }
    }
    onCancel();
  };
  const fmt = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 14px", background:T.dangerBg, borderRadius:30, border:`1px solid ${T.danger}22` }}>
        <div style={{ width:10, height:10, borderRadius:"50%", background:T.danger, animation:"recPulse 1s infinite", flexShrink:0 }} />
        <span style={{ color:T.danger, fontWeight:600, fontSize:14, fontVariantNumeric:"tabular-nums", minWidth:40 }}>{fmt(seconds)}</span>
        <button onClick={cancel} style={{ background:"none", border:"none", fontSize:18, padding:"0 4px", color:T.slate400 }}>🗑</button>
        <button onClick={stop} style={{ background:T.danger, border:"none", borderRadius:"50%", width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:"#fff" }}>⬛</button>
      </div>
      {liveText && (
        <div style={{ background:T.blue50, borderRadius:10, padding:"5px 12px", fontSize:12, color:T.blue700, maxWidth:360, border:`1px solid ${T.blue200}` }}>
          <span style={{ fontSize:10, fontWeight:700, color:T.blue500, marginRight:6 }}>🎙 LIVE:</span>
          {liveText}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   CONTEXT MENU
══════════════════════════════════════════════════════════ */
function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(()=>document.addEventListener("mousedown",h),0);
    return ()=>document.removeEventListener("mousedown",h);
  }, []);
  return (
    <div ref={ref} className="ctx-menu" style={{ position:"fixed", left:x, top:y, zIndex:99999, background:T.white, borderRadius:12, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", border:`1px solid ${T.slate200}`, minWidth:180, overflow:"hidden" }}>
      {items.map((item,i) => item==="—" ? (
        <div key={i} style={{ height:1, background:T.slate100, margin:"4px 0" }} />
      ) : (
        <div key={i} onClick={()=>{ item.action(); onClose(); }}
          style={{ padding:"9px 14px", fontSize:13.5, fontWeight:500, cursor:"pointer", color:item.danger?T.danger:T.slate700, display:"flex", alignItems:"center", gap:10 }}
          onMouseEnter={e=>e.currentTarget.style.background=item.danger?T.dangerBg:T.slate50}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <span style={{ fontSize:15 }}>{item.icon}</span> {item.label}
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   EMOJI PICKER
══════════════════════════════════════════════════════════ */
function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(()=>document.addEventListener("mousedown",h),0);
    return ()=>document.removeEventListener("mousedown",h);
  }, []);
  return (
    <div ref={ref} style={{ position:"absolute", bottom:60, right:8, zIndex:5000, background:T.white, borderRadius:14, border:`1px solid ${T.slate200}`, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", padding:10, display:"flex", flexWrap:"wrap", gap:4, width:270, animation:"slideUp .18s ease" }}>
      {EMOJIS.map(e=>(
        <button key={e} onClick={()=>{ onSelect(e); onClose(); }}
          style={{ background:"none", border:"none", fontSize:20, padding:5, borderRadius:6 }}
          onMouseEnter={x=>{ x.currentTarget.style.transform="scale(1.25)"; x.currentTarget.style.background=T.slate100; }}
          onMouseLeave={x=>{ x.currentTarget.style.transform="scale(1)"; x.currentTarget.style.background="none"; }}>
          {e}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   VOICE PLAYBACK
══════════════════════════════════════════════════════════ */
function VoicePlayback({ url, voiceBase64, voiceMime, liveTranscript, duration, isMine, translateEnabled, targetLang }) {
  const [playing, setPlaying]           = useState(false);
  const [progress, setProgress]         = useState(0);
  const [curTime, setCurTime]           = useState(0);
  const [speed, setSpeed]               = useState(1);
  const [result, setResult]             = useState(null);
  const [processing, setProcessing]     = useState(false);
  const audioRef = useRef(null);
  // Stable audio instance — only recreate when url changes
  if (!audioRef.current || audioRef.current._url !== url) {
    if (audioRef.current) { audioRef.current.pause(); }
    audioRef.current = new Audio(url);
    audioRef.current._url = url;
  }

  useEffect(() => {
    const a = audioRef.current;
    a.playbackRate = speed;
    a.ontimeupdate = () => { setProgress(a.duration ? a.currentTime/a.duration : 0); setCurTime(a.currentTime||0); };
    a.onended = () => { setPlaying(false); setProgress(0); setCurTime(0); };
    return () => { a.pause(); a.ontimeupdate = null; a.onended = null; };
  }, [url]);

  useEffect(() => { audioRef.current.playbackRate = speed; }, [speed]);

  const cycleSpeed = () => setSpeed(s => s===1 ? 1.5 : s===1.5 ? 2 : 1);

  const seekTo = (e) => {
    const a = audioRef.current;
    if (!a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * a.duration;
    setProgress(pct); setCurTime(a.currentTime);
  };

  const fmtTime = s => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;

  // Auto-transcribe + translate — only run once per (url+targetLang) combination, not on every render
  const autoTranscribeKey = `${url}__${targetLang}`;
  const autoTranscribedRef = useRef({});
  useEffect(() => {
    if (!translateEnabled) return;
    if (!targetLang || targetLang === "auto") return;
    if (!liveTranscript && !url) return;
    if (result && !result.error) return;
    if (autoTranscribedRef.current[autoTranscribeKey]) return; // already ran for this key
    autoTranscribedRef.current[autoTranscribeKey] = true;

    let cancelled = false;
    setProcessing(true);
    setResult(null);

    transcribeAndTranslate(url, voiceBase64, voiceMime, liveTranscript, true, targetLang).then(res => {
      if (!cancelled) { setResult(res); setProcessing(false); }
    });

    return () => { cancelled = true; };
  }, [autoTranscribeKey, translateEnabled]);

  useEffect(() => {
    if (!translateEnabled) { setResult(null); }
  }, [translateEnabled]);

  useEffect(() => {
    // When language changes, allow re-transcription
    setResult(null);
  }, [targetLang]);

  const handleTranscribe = async () => {
    if (processing) return;
    setProcessing(true);
    setResult(null);
    const res = await transcribeAndTranslate(url, voiceBase64, voiceMime, liveTranscript, translateEnabled, targetLang);
    setResult(res);
    setProcessing(false);
  };

  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const barColor   = isMine ? T.blue600 : T.slate700;
  const trackColor = isMine ? T.blue200 : T.slate200;

  return (
    <div style={{ minWidth:200 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <button onClick={toggle} style={{ width:34, height:34, borderRadius:"50%", background:barColor, border:"none", fontSize:13, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          {playing ? "⏸" : "▶"}
        </button>
        <div onClick={seekTo} style={{ flex:1, height:8, display:"flex", alignItems:"center", cursor:"pointer" }}>
          <div style={{ width:"100%", height:3, background:trackColor, borderRadius:3, overflow:"hidden", position:"relative" }}>
            <div style={{ height:"100%", width:`${progress*100}%`, background:barColor, borderRadius:3, transition:"width .1s" }} />
          </div>
        </div>
        <span style={{ fontSize:11, color:isMine?T.blue600:T.slate400, minWidth:56, textAlign:"right" }}>
          {playing || curTime>0 ? fmtTime(curTime) : (duration ? `0:${String(duration).padStart(2,"0")}` : "0:00")}
        </span>
        <button onClick={cycleSpeed} title="Playback speed"
          style={{ background: isMine?"rgba(255,255,255,0.2)":T.slate100, border:`1px solid ${isMine?"rgba(255,255,255,0.3)":T.slate200}`, borderRadius:6, padding:"2px 6px", fontSize:10.5, fontWeight:700, color:isMine?"#fff":T.slate600, flexShrink:0, minWidth:30 }}>
          {speed}x
        </button>
        <button onClick={handleTranscribe} disabled={processing}
          title={translateEnabled && targetLang && targetLang!=="auto"
            ? `Transcribe & translate to ${LANGUAGES.find(l=>l.code===targetLang)?.label||targetLang}`
            : "Transcribe voice message"}
          style={{
            background: processing ? T.slate100 : isMine ? "rgba(255,255,255,0.2)" : T.blue50,
            border: `1px solid ${isMine?"rgba(255,255,255,0.3)":T.blue200}`,
            borderRadius:6, padding:"2px 7px", fontSize:12,
            color: isMine?"#fff":T.blue600, cursor:processing?"default":"pointer", flexShrink:0,
          }}>
          {processing
            ? <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ width:10, height:10, border:`2px solid ${isMine?"rgba(255,255,255,0.5)":T.blue300}`, borderTopColor:isMine?"#fff":T.blue600, borderRadius:"50%", animation:"spin .7s linear infinite", display:"inline-block" }} />
              </span>
            : "🌐"}
        </button>
      </div>

      {liveTranscript && !result && (
        <div style={{ marginTop:5, fontSize:11, color:isMine?"rgba(255,255,255,0.7)":T.teal, fontStyle:"italic", borderLeft:`2px solid ${isMine?"rgba(255,255,255,0.4)":T.teal}`, paddingLeft:6 }}>
          🎙 {liveTranscript.length > 60 ? liveTranscript.slice(0,60)+"…" : liveTranscript}
        </div>
      )}

      {processing && !result && (
        <div style={{ marginTop:5, display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:12, height:12, border:`2px solid ${isMine?"rgba(255,255,255,0.5)":T.teal}`, borderTopColor:isMine?"#fff":T.teal, borderRadius:"50%", animation:"spin .7s linear infinite" }} />
          <span style={{ fontSize:11, color:isMine?"rgba(255,255,255,0.7)":T.teal }}>
            Translating to {LANGUAGES.find(l=>l.code===targetLang)?.label||targetLang}…
          </span>
        </div>
      )}

      {result && (
        <div style={{
          marginTop:6, background:isMine?"rgba(255,255,255,0.15)":T.blue50,
          borderRadius:8, padding:"8px 10px", fontSize:12.5,
          color:isMine?"#fff":T.slate700,
          borderLeft:`3px solid ${isMine?"rgba(255,255,255,0.4)":T.blue400}`,
          maxWidth:280,
        }}>
          {result.error ? (
            <div style={{ color:isMine?"rgba(255,255,255,0.8)":T.danger, fontSize:12 }}>⚠️ {result.error}</div>
          ) : (
            <>
              <div style={{ fontSize:10, fontWeight:700, color:isMine?"rgba(255,255,255,0.65)":T.blue500, marginBottom:4 }}>
                🎙 TRANSCRIPTION {result.translation ? `+ 🌐 ${result.targetLabel?.toUpperCase()} TRANSLATION` : ""}
              </div>
              <div style={{ marginBottom: result.translation ? 6 : 0 }}>
                <div style={{ fontSize:10, fontWeight:600, color:isMine?"rgba(255,255,255,0.5)":T.slate400, marginBottom:2, textTransform:"uppercase", letterSpacing:0.5 }}>Original:</div>
                <div style={{ lineHeight:1.5 }}>{result.transcription}</div>
              </div>
              {result.translation && (
                <div style={{ borderTop:`1px solid ${isMine?"rgba(255,255,255,0.2)":T.slate200}`, paddingTop:6, marginTop:4 }}>
                  <div style={{ fontSize:10, fontWeight:600, color:isMine?"rgba(255,255,255,0.5)":T.teal, marginBottom:2, textTransform:"uppercase", letterSpacing:0.5 }}>{result.targetLabel}:</div>
                  <div style={{ lineHeight:1.5, color:isMine?"rgba(255,255,255,0.95)":T.slate800, fontWeight:500 }}>{result.translation}</div>
                </div>
              )}
            </>
          )}
          <button onClick={()=>setResult(null)} style={{ marginTop:6, background:"none", border:"none", fontSize:10, color:isMine?"rgba(255,255,255,0.5)":T.slate400, cursor:"pointer", padding:0 }}>✕ dismiss</button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   TRANSLATION CACHE — Map-based, only caches successes
══════════════════════════════════════════════════════════ */
// Fresh cache every app load — no stale untranslated results
const translationCache = new Map();

/* ──── stripMentionTags ────
   For places that show a plain-text preview of a message (reply preview,
   pinned banner, sidebar last-message) and don't run it through renderText —
   these should still show "@Name" rather than the internal "@Name[id]" tag. */
function stripMentionTags(text) {
  if (!text) return text;
  return text.replace(/(@\w[\w\s]*)\[[^\]]+\]/g, "$1");
}

/* ──── renderText ──── */
function renderText(text) {
  if (!text) return null;
  const parts = text.split(/(@\w[\w\s]*\[[^\]]+\]|@\w+)/g);
  return parts.map((part,i) => {
    if (part.startsWith("@")) {
      // Strip the trailing "[id]" tag used internally to resolve the
      // mentioned member's id — the user should only ever see "@Name".
      const label = part.replace(/\[[^\]]+\]$/, "");
      return <span key={i} style={{ color:T.blue600, fontWeight:600, background:T.blue50, borderRadius:4, padding:"0 3px" }}>{label}</span>;
    }
    return part;
  });
}

/* ══════════════════════════════════════════════════════════
   BUBBLE COMPONENT
══════════════════════════════════════════════════════════ */
function Bubble({ msg, isMine, senderName, senderColor, onCtx, replyMsg, replySenderName, isStarred, translateEnabled, targetLang, onView }) {
  const isCall  = msg.callType != null;
  const isVoice = msg.type === "voice";
  const isImage = msg.type === "image";
  const isFile  = msg.type === "file";

  const [translated, setTranslated]     = useState(null);
  const [translating, setTranslating]   = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const cacheKey = `${msg.id}__${targetLang}`;

  /* ── AUTO-TRANSLATE incoming text messages ── */
  useEffect(() => {
    if (isMine) return;
    if (!translateEnabled) return;
    if (!msg.text || isCall || isVoice || isImage || isFile) return;
    if (!targetLang || targetLang === "auto") return;

    // Check cache — only use genuinely translated results
    if (translationCache.has(cacheKey)) {
      const cached = translationCache.get(cacheKey);
      if (cached && cached.trim() && cached.toLowerCase() !== msg.text.toLowerCase()) {
        setTranslated(cached);
        setTranslating(false);
        return;
      }
      translationCache.delete(cacheKey); // stale — retry
    }

    let cancelled = false;
    setTranslating(true);
    setTranslated(null);
    setShowOriginal(false);

    translateWithClaude(msg.text, targetLang).then(result => {
      if (cancelled) return;
      const finalResult = (result || "").trim();
      if (finalResult && finalResult.toLowerCase() !== msg.text.toLowerCase()) {
        translationCache.set(cacheKey, finalResult);
        setTranslated(finalResult);
      } else if (finalResult && targetLang === "en") {
        // Source was already English — no translation needed, don't show
        setTranslated(null);
      } else {
        setTranslated(null); // don't cache failures
      }
      setTranslating(false);
    }).catch(() => {
      if (!cancelled) { setTranslated(null); setTranslating(false); }
    });

    return () => { cancelled = true; };
  }, [msg.id, msg.text, isMine, translateEnabled, targetLang, cacheKey, isCall, isVoice, isImage, isFile]);

  // Reset when translate toggled off
  useEffect(() => {
    if (!translateEnabled) {
      setTranslated(null);
      setTranslating(false);
      setShowOriginal(false);
    }
  }, [translateEnabled]);

  // Reset when target language changes
  useEffect(() => {
    setTranslated(null);
    setTranslating(false);
    setShowOriginal(false);
  }, [targetLang]);

  // Manual translate for own messages
  const [myTranslated, setMyTranslated] = useState(null);
  const [myTranslating, setMyTranslating] = useState(false);
  const [myTranslateErr, setMyTranslateErr] = useState(null);

  const manualTranslate = async () => {
    if (myTranslating) return;
    setMyTranslating(true);
    setMyTranslateErr(null);
    try {
      const result = await translateWithClaude(msg.text, targetLang);
      const final = (result || "").trim();
      if (final) {
        setMyTranslated(final);
      } else {
        setMyTranslateErr("Translation unavailable");
      }
    } catch(e) {
      setMyTranslateErr("Translation failed: " + e.message);
    }
    setMyTranslating(false);
  };

  useEffect(() => { setMyTranslated(null); }, [targetLang]);

  const callLabel = () => {
    if (msg.callStatus==="missed")    return `Missed ${msg.callType} call`;
    if (msg.callStatus==="rejected")  return `${msg.callType} call declined`;
    if (msg.callStatus==="cancelled") return `Call cancelled`;
    return `${msg.callType} call ended${msg.callDur?" · "+msg.callDur:""}`;
  };
  const callIcon  = () => (msg.callStatus==="missed"||msg.callStatus==="rejected"||msg.callStatus==="cancelled") ? "📵" : msg.callType==="video" ? "📹" : "📞";
  const callColor = () => (msg.callStatus==="missed"||msg.callStatus==="rejected") ? T.danger : T.success;

  const bubbleBg    = isMine ? T.blue600 : T.white;
  const textColor   = isMine ? "#fff" : T.slate800;
  const timeColor   = isMine ? "rgba(255,255,255,.6)" : T.slate400;
  const borderStyle = isMine ? "none" : `1px solid ${T.slate200}`;
  const borderRadius = isMine ? "18px 6px 18px 18px" : "6px 18px 18px 18px";

  const hasTranslation = !isMine
    && translated !== null
    && translated !== undefined
    && translated.trim().length > 0;

  const displayText = (hasTranslation && !showOriginal) ? translated : msg.text;

  return (
    <div className="bubble-wrap" style={{ display:"flex", flexDirection:"column", alignItems:isMine?"flex-end":"flex-start", marginBottom:3, animation:"slideUp .12s ease", width:"100%", minWidth:0 }}>
      {!isMine && senderName && (
        <div style={{ fontSize:11.5, fontWeight:600, color:senderColor||T.blue600, marginLeft:12, marginBottom:3 }}>{senderName}</div>
      )}
      {msg.isPrivate && (
        <div style={{ fontSize:11, color:T.blue500, marginLeft:isMine?0:12, marginRight:isMine?12:0, marginBottom:2, fontStyle:"italic" }}>🔒 Private</div>
      )}
      <div style={{ display:"flex", alignItems:"flex-end", gap:6, flexDirection:isMine?"row-reverse":"row", justifyContent:"flex-start", width:"100%", minWidth:0 }}>
        <div className="bubble-actions" style={{ opacity:0, transition:"opacity .15s", display:"flex", gap:3, paddingBottom:6 }}>
          <button onClick={e=>onCtx(e,msg)} style={{ background:T.white, border:`1px solid ${T.slate200}`, borderRadius:8, padding:"2px 8px", fontSize:13, color:T.slate500, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>⋯</button>
        </div>
        <div className="msg-bubble" onContextMenu={e=>{ e.preventDefault(); onCtx(e,msg); }}
          style={{ maxWidth:"58%", minWidth:0, overflowWrap:"anywhere", wordBreak:"break-word", padding:isCall?"10px 14px":isImage?"6px":"9px 12px", background:bubbleBg, borderRadius, border:borderStyle, boxShadow:isMine?"0 2px 8px rgba(37,99,235,0.2)":"0 1px 4px rgba(0,0,0,0.06)" }}>
          {replyMsg && (
            <div style={{ borderLeft:`3px solid ${isMine?"rgba(255,255,255,.5)":T.blue500}`, paddingLeft:8, marginBottom:7, background:isMine?"rgba(255,255,255,.12)":T.blue50, borderRadius:"0 6px 6px 0", padding:"4px 8px 4px 10px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:isMine?"rgba(255,255,255,.8)":T.blue500 }}>{replySenderName}</div>
              <div style={{ fontSize:12, color:isMine?"rgba(255,255,255,.6)":T.slate500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:200 }}>
                {stripMentionTags(replyMsg.text)||(replyMsg.type==="voice"?"🎤 Voice":replyMsg.type==="image"?"📷 Image":"📎 File")}
              </div>
            </div>
          )}
          {isCall ? (
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:callColor()+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{callIcon()}</div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:callColor() }}>{callLabel()}</div>
                <div style={{ fontSize:11, color:T.slate400 }}>Tap to call back</div>
              </div>
            </div>
          ) : isVoice ? (
            <VoicePlayback
              url={msg.voiceUrl}
              voiceBase64={msg.voiceBase64}
              voiceMime={msg.voiceMime}
              liveTranscript={msg.liveTranscript}
              duration={msg.duration}
              isMine={isMine}
              translateEnabled={translateEnabled}
              targetLang={targetLang}
            />
          ) : isImage ? (
            <div>
              <div style={{ position:"relative", display:"inline-block" }}>
                <img src={mediaSrc(msg.imageUrl)} alt="img" style={{ maxWidth:200, maxHeight:180, borderRadius:10, display:"block", cursor:"pointer", opacity:msg.uploading?0.55:1 }} onClick={()=>onView?.(msg)} />
                {msg.uploading && <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:15, textShadow:"0 1px 4px rgba(0,0,0,.6)" }}>{msg.progress||0}%</div>}
              </div>
              {msg.caption && <div style={{ fontSize:13, color:textColor, marginTop:5, paddingLeft:2, overflowWrap:"anywhere", whiteSpace:"pre-wrap" }}>{msg.caption}</div>}
            </div>
          ) : isFile ? (
            <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }} onClick={()=>onView?.(msg)}>
              <div style={{ width:38, height:38, borderRadius:10, background:isMine?"rgba(255,255,255,.15)":T.slate100, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>📎</div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:textColor, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{msg.fileName}</div>
                <div style={{ fontSize:11, color:isMine?"rgba(255,255,255,.6)":T.slate400 }}>{msg.uploading ? `Uploading… ${msg.progress||0}% of ${msg.fileSize}` : msg.fileSize}</div>
                {msg.uploading && <div style={{ height:4, borderRadius:2, background:"rgba(255,255,255,.25)", marginTop:5, width:140, overflow:"hidden" }}><div style={{ height:"100%", width:`${msg.progress||0}%`, background:"#fff", transition:"width .2s" }} /></div>}
              </div>
            </div>
          ) : (
            <div>
              {msg.forwardedFrom && <div style={{ fontSize:11, color:isMine?"rgba(255,255,255,.6)":T.slate400, fontStyle:"italic", marginBottom:4 }}>↪ Forwarded from {msg.forwardedFrom}</div>}
              {msg.edited && <span style={{ fontSize:11, color:isMine?"rgba(255,255,255,.5)":T.slate400 }}>(edited) </span>}

              {!isMine && translating && (
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                  <div style={{ width:12, height:12, border:`2px solid ${T.teal}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
                  <span style={{ fontSize:11, color:T.teal }}>Translating to {LANGUAGES.find(l=>l.code===targetLang)?.label||targetLang}…</span>
                </div>
              )}

              <div style={{ fontSize:14, lineHeight:1.5, color:textColor, wordBreak:"break-word", overflowWrap:"anywhere", whiteSpace:"pre-wrap", minWidth:0 }}>
                {renderText(displayText)}
              </div>

              {/* Incoming: translation toggle footer */}
              {!isMine && translateEnabled && !translating && hasTranslation && (
                <div style={{ marginTop:5 }}>
                  {!showOriginal ? (
                    <div style={{ fontSize:10.5, color:T.slate400, borderTop:`1px solid ${T.slate100}`, paddingTop:4, marginTop:4, display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:T.teal }}>🌐 {LANGUAGES.find(l=>l.code===targetLang)?.label||targetLang}</span>
                      <span onClick={()=>setShowOriginal(true)} style={{ cursor:"pointer", textDecoration:"underline", color:T.slate400, fontSize:10.5 }}>Show original</span>
                    </div>
                  ) : (
                    <div style={{ fontSize:12, color:T.slate500, borderTop:`1px solid ${T.slate100}`, paddingTop:4, marginTop:4 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:T.teal, marginBottom:2 }}>Original:</div>
                      <div style={{ fontStyle:"italic" }}>{msg.text}</div>
                      <span onClick={()=>setShowOriginal(false)} style={{ cursor:"pointer", textDecoration:"underline", color:T.slate400, fontSize:10.5 }}>Show translation</span>
                    </div>
                  )}
                </div>
              )}

              {/* Outgoing: manual translate button */}
              {isMine && translateEnabled && msg.text && !isCall && (
                <div style={{ marginTop:4 }}>
                  {myTranslated === null ? (
                    <button onClick={manualTranslate} disabled={myTranslating}
                      style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:6, padding:"2px 8px", fontSize:10.5, color:"#fff", cursor:myTranslating?"default":"pointer", display:"flex", alignItems:"center", gap:4 }}>
                      {myTranslating
                        ? <><span style={{ width:10, height:10, border:"2px solid rgba(255,255,255,0.5)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin .7s linear infinite", display:"inline-block" }} /> Translating…</>
                        : `🌐 Translate to ${LANGUAGES.find(l=>l.code===targetLang)?.label||targetLang}`
                      }
                    </button>
                  ) : myTranslated ? (
                    <div style={{ marginTop:4 }}>
                      <div style={{ background:"rgba(255,255,255,0.15)", borderRadius:8, padding:"5px 8px", fontSize:12, color:"#fff", borderLeft:"2px solid rgba(255,255,255,0.4)", marginBottom:3 }}>
                        <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.6)", marginBottom:2 }}>🌐 {LANGUAGES.find(l=>l.code===targetLang)?.label||targetLang}:</div>
                        {myTranslated}
                      </div>
                      <button onClick={()=>{ setMyTranslated(null); setMyTranslateErr(null); }} style={{ background:"none", border:"none", fontSize:10, color:"rgba(255,255,255,0.5)", cursor:"pointer", padding:0 }}>✕ hide</button>
                    </div>
                  ) : myTranslateErr ? (
                    <div style={{ marginTop:4, fontSize:10, color:"rgba(255,100,100,0.9)", background:"rgba(255,0,0,0.15)", borderRadius:6, padding:"3px 8px" }}>⚠️ {myTranslateErr}</div>
                  ) : null}
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize:10.5, color:timeColor, textAlign:"right", marginTop:isImage?5:4, display:"flex", justifyContent:"flex-end", alignItems:"center", gap:3 }}>
            {isStarred && <span style={{ fontSize:11, color:"#F59E0B" }}>⭐</span>}
            {msg.t}
            {isMine && (msg.failed
              ? <span title={msg.failReason||"Not sent"} style={{ color:"#FCA5A5", fontSize:11, fontWeight:700 }}>⚠ Not sent — right-click to retry</span>
              : msg.pending
                ? <span title="Sending…" style={{ color:"rgba(255,255,255,.7)", fontSize:11 }}>🕒</span>
                : <span title="Saved & sent" style={{ color:"rgba(255,255,255,.7)", fontSize:12 }}>✓</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   INPUT BAR
══════════════════════════════════════════════════════════ */
function InputBar({ onSend, onSendFile, replyTo, replyName, onCancelReply, members, targetLang, translateEnabled, onOpenCamera }) {
  const [text, setText]           = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showMention, setShowMention] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showAttach, setShowAttach] = useState(false);
  const inputRef = useRef();
  const fileRef  = useRef();
  const imgRef   = useRef();

  const send = () => {
    if (!text.trim()) return;
    onSend({ type:"text", text:text.trim() });
    setText(""); setShowMention(false);
    inputRef.current?.focus();
  };
  const handleKeyDown = e => {
    if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    if (e.key==="Backspace" && text==="") onCancelReply?.();
  };
  const handleChange = e => {
    const val = e.target.value; setText(val);
    const lastAt = val.lastIndexOf("@");
    if (lastAt!==-1 && lastAt===val.length-1) { setShowMention(true); setMentionQuery(""); }
    else if (lastAt!==-1 && lastAt<val.length) {
      const q = val.slice(lastAt+1);
      if (!/\s/.test(q)) { setShowMention(true); setMentionQuery(q); }
      else setShowMention(false);
    } else setShowMention(false);
  };
  const insertMention = m => {
    const lastAt = text.lastIndexOf("@");
    setText(text.slice(0,lastAt)+`@${m.name}[${m.id}]`);
    setShowMention(false); inputRef.current?.focus();
  };
  const filteredMembers = (members||[]).filter(m =>
    m.name.toLowerCase().includes(mentionQuery.toLowerCase()) || m.id.toLowerCase().includes(mentionQuery.toLowerCase())
  );
  // Files (any type, up to 600 MB) are uploaded by the app and then sent as a normal chat message.
  const sendFile = e => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    files.forEach(f => onSendFile(f));
  };
  const iconBtn = (emoji,onClick,title) => (
    <button onClick={onClick} title={title} style={{ background:"none", border:"none", fontSize:18, color:T.slate400, padding:"6px", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}
      onMouseEnter={e=>{ e.currentTarget.style.color=T.blue500; e.currentTarget.style.background=T.blue50; }}
      onMouseLeave={e=>{ e.currentTarget.style.color=T.slate400; e.currentTarget.style.background="none"; }}>
      {emoji}
    </button>
  );

  return (
    <div style={{ background:T.white, borderTop:`1px solid ${T.slate200}`, padding:"8px 12px", position:"relative", boxShadow:"0 -4px 16px rgba(0,0,0,0.04)" }}>
      {replyTo && (
        <div style={{ display:"flex", alignItems:"center", background:T.blue50, borderRadius:10, padding:"7px 12px", marginBottom:8, borderLeft:`3px solid ${T.blue500}`, gap:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11.5, fontWeight:700, color:T.blue600 }}>{replyName}</div>
            <div style={{ fontSize:12, color:T.slate500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:280 }}>
              {stripMentionTags(replyTo.text)||(replyTo.type==="voice"?"🎤 Voice":replyTo.type==="image"?"📷 Image":"📎 File")}
            </div>
          </div>
          <button onClick={onCancelReply} style={{ background:"none", border:"none", color:T.slate400, fontSize:16 }}>✕</button>
        </div>
      )}
      {showMention && filteredMembers.length>0 && (
        <div style={{ position:"absolute", bottom:"100%", left:12, right:12, background:T.white, border:`1px solid ${T.slate200}`, borderRadius:12, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", overflow:"hidden", zIndex:1000, marginBottom:4 }}>
          {filteredMembers.map(m=>(
            <div key={m.id} onClick={()=>insertMention(m)}
              style={{ padding:"8px 14px", cursor:"pointer", display:"flex", gap:10, alignItems:"center", borderBottom:`1px solid ${T.slate100}` }}
              onMouseEnter={e=>e.currentTarget.style.background=T.slate50}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Avatar user={m} size={30} />
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:T.slate800 }}>{m.name}</div>
                <div style={{ fontSize:11, color:T.slate400 }}>{m.role} · {m.id}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {recording ? (
        <VoiceRecorder
          onSend={v => { onSend(v); setRecording(false); }}
          onCancel={() => setRecording(false)}
          targetLang={targetLang}
          translateEnabled={translateEnabled}
        />
      ) : (
        <div style={{ display:"flex", alignItems:"center", gap:4, position:"relative" }}>
          <input ref={fileRef} type="file" multiple style={{ display:"none" }} onChange={sendFile} />
          <input ref={imgRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={sendFile} />
          <div style={{ position:"relative" }}>
            {iconBtn(showAttach?"✕":"➕",()=>setShowAttach(s=>!s),"Attach media")}
            {showAttach && (
              <>
                <div onClick={()=>setShowAttach(false)} style={{ position:"fixed", inset:0, zIndex:999 }} />
                <div style={{ position:"absolute", bottom:"100%", left:0, marginBottom:8, background:T.white, border:`1px solid ${T.slate200}`, borderRadius:14, boxShadow:"0 8px 24px rgba(0,0,0,0.14)", overflow:"hidden", zIndex:1000, minWidth:180 }}>
                  {[
                    { icon:"📷", label:"Camera", bg:T.rose,  bgSoft:T.roseBg,  onClick:()=>{ setShowAttach(false); onOpenCamera?.(); } },
                    { icon:"🖼️", label:"Gallery", bg:T.violet, bgSoft:T.violetBg, onClick:()=>{ setShowAttach(false); imgRef.current?.click(); } },
                    { icon:"📎", label:"Document", bg:T.blue600, bgSoft:T.blue50, onClick:()=>{ setShowAttach(false); fileRef.current?.click(); } },
                  ].map(o=>(
                    <div key={o.label} onClick={o.onClick}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", cursor:"pointer" }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.slate50}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:o.bgSoft, color:o.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>{o.icon}</div>
                      <span style={{ fontSize:13.5, fontWeight:600, color:T.slate700 }}>{o.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div style={{ flex:1, background:T.slate50, borderRadius:22, padding:"8px 14px", border:`1.5px solid ${T.slate200}`, display:"flex", alignItems:"center" }}
            onFocusCapture={e=>e.currentTarget.style.borderColor=T.blue400}
            onBlurCapture={e=>e.currentTarget.style.borderColor=T.slate200}>
            <textarea ref={inputRef} value={text} onChange={handleChange} onKeyDown={handleKeyDown}
              placeholder={members ? "Type a message… use @ to mention" : "Type a message…"}
              rows={1}
              style={{ flex:1, border:"none", outline:"none", fontSize:14, background:"transparent", color:T.slate800, resize:"none", lineHeight:1.5, maxHeight:96, overflowY:"auto" }} />
          </div>
          {iconBtn("😊",()=>setShowEmoji(s=>!s),"Emoji")}
          {showEmoji && <EmojiPicker onSelect={e=>setText(t=>t+e)} onClose={()=>setShowEmoji(false)} />}
          {text.trim() ? (
            <button onClick={send} style={{ width:40, height:40, borderRadius:"50%", border:"none", background:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, color:"#fff", boxShadow:`0 2px 10px rgba(37,99,235,0.35)`, flexShrink:0 }}
              onMouseEnter={e=>e.currentTarget.style.background=T.blue700}
              onMouseLeave={e=>e.currentTarget.style.background=T.blue600}>
              ➤
            </button>
          ) : (
            <button onClick={()=>setRecording(true)} style={{ width:40, height:40, borderRadius:"50%", border:"none", background:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, color:"#fff", boxShadow:`0 2px 10px rgba(37,99,235,0.35)`, flexShrink:0 }}>
              🎤
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const isVideoFile = (msg) => msg.type==="file" && ((msg.fileType||"").startsWith("video/") || /\.(mp4|webm|ogg|mov|m4v)$/i.test(msg.fileName||""));

function MediaViewer({ msg, items, onNavigate, onClose }) {
  const [scale, setScale] = useState(1);
  useEffect(() => { setScale(1); }, [msg.id]);

  const list = items && items.length ? items : [msg];
  const idx = list.findIndex(m => m.id === msg.id);
  const hasPrev = idx > 0;
  const hasNext = idx >= 0 && idx < list.length - 1;
  const goPrev = () => { if (hasPrev) onNavigate?.(list[idx-1].id); };
  const goNext = () => { if (hasNext) onNavigate?.(list[idx+1].id); };

  useEffect(() => {
    const onKey = e => {
      if (e.key==="ArrowLeft") goPrev();
      else if (e.key==="ArrowRight") goNext();
      else if (e.key==="Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, list, onClose]);

  const isImage = msg.type === "image";
  const isVideoF = isVideoFile(msg);
  const isPdf = msg.type === "file" && !isVideoF && ((msg.fileType||"").includes("pdf") || (msg.fileName||"").toLowerCase().endsWith(".pdf"));

  const download = () => {
    try {
      const src = isImage ? msg.imageUrl : msg.fileUrl;
      if (src && src.startsWith("data:")) {
        const arr = src.split(","); const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]); let n = bstr.length; const u8 = new Uint8Array(n);
        while(n--) u8[n]=bstr.charCodeAt(n);
        const blob = new Blob([u8],{type:mime});
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = blobUrl; a.download = msg.fileName || (isImage ? "photo.jpg" : "file");
        document.body.appendChild(a); a.click();
        setTimeout(()=>{ document.body.removeChild(a); URL.revokeObjectURL(blobUrl); },2000);
      } else if (src) {
        // big files are streamed straight to disk by the browser (not loaded into memory)
        const a = document.createElement("a"); a.href = mediaSrc(src,{download:true}); a.download = msg.fileName || "file";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
    } catch(e) { alert("Could not download: "+e.message); }
  };

  const canShare = typeof navigator!=="undefined" && !!navigator.share;
  const share = async () => {
    try {
      const src = isImage ? msg.imageUrl : msg.fileUrl;
      if (src && src.startsWith("data:") && navigator.canShare) {
        const blob = await (await fetch(src)).blob();
        const file = new File([blob], msg.fileName || (isImage?"photo.jpg":"file"), { type: blob.type });
        if (navigator.canShare({ files:[file] })) { await navigator.share({ files:[file] }); return; }
      }
      await navigator.share({ title: msg.fileName || "Shared from IDK" });
    } catch(e) {}
  };

  const iconBtnSt = { background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.2)", borderRadius:10, color:"#fff", fontSize:14, fontWeight:600, padding:"8px 12px", flexShrink:0 };
  const navBtnSt = side => ({ position:"absolute", [side]:12, top:"50%", transform:"translateY(-50%)", width:44, height:44, borderRadius:"50%", background:"rgba(255,255,255,.12)", border:"1px solid rgba(255,255,255,.2)", color:"#fff", fontSize:22, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" });

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9500, background:"rgba(15,23,42,0.96)", backdropFilter:"blur(6px)", display:"flex", flexDirection:"column", animation:"fadeIn .15s ease" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 18px", flexShrink:0, flexWrap:"wrap" }}>
        <button onClick={onClose} style={{ ...iconBtnSt, display:"flex", alignItems:"center", gap:6 }}>← Back</button>
        <div style={{ color:"#fff", fontSize:14, fontWeight:600, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {isImage ? "Photo" : (msg.fileName || "Document")}{list.length>1 ? ` · ${idx+1}/${list.length}` : ""}
        </div>
        {isImage && <>
          <button onClick={()=>setScale(s=>Math.max(1,+(s-0.25).toFixed(2)))} style={iconBtnSt} title="Zoom out">−</button>
          <span style={{ color:"#fff", fontSize:12, minWidth:38, textAlign:"center" }}>{Math.round(scale*100)}%</span>
          <button onClick={()=>setScale(s=>Math.min(4,+(s+0.25).toFixed(2)))} style={iconBtnSt} title="Zoom in">+</button>
        </>}
        {canShare && <button onClick={share} style={iconBtnSt} title="Share">⤴ Share</button>}
        <button onClick={download} style={{ ...iconBtnSt, background:T.blue600, borderColor:T.blue600 }}>⬇ Download</button>
      </div>
      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", overflow:"auto", padding:20, position:"relative" }}>
        {hasPrev && <button onClick={goPrev} style={navBtnSt("left")} title="Previous">‹</button>}
        {isImage ? (
          <img src={mediaSrc(msg.imageUrl)} alt="Full size" style={{ maxWidth:"92vw", maxHeight:"82vh", objectFit:"contain", borderRadius:8, transform:`scale(${scale})`, transition:"transform .15s ease" }} />
        ) : isVideoF ? (
          <video src={mediaSrc(msg.fileUrl)} controls autoPlay style={{ maxWidth:"92vw", maxHeight:"82vh", borderRadius:10, background:"#000" }} />
        ) : isPdf ? (
          <iframe title={msg.fileName||"document"} src={mediaSrc(msg.fileUrl)} style={{ width:"90vw", height:"82vh", border:"none", borderRadius:10, background:"#fff" }} />
        ) : (
          <div style={{ textAlign:"center", color:"#fff" }}>
            <div style={{ fontSize:48, marginBottom:14 }}>📎</div>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>{msg.fileName}</div>
            <div style={{ fontSize:12.5, color:"rgba(255,255,255,.6)", marginBottom:18 }}>{msg.fileSize} · Preview not available for this file type</div>
            <button onClick={download} style={{ background:T.blue600, border:"none", borderRadius:10, color:"#fff", fontSize:14, fontWeight:600, padding:"10px 22px" }}>⬇ Download to view</button>
          </div>
        )}
        {hasNext && <button onClick={goNext} style={navBtnSt("right")} title="Next">›</button>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   CONVERSATION ROW
══════════════════════════════════════════════════════════ */
function ConvRow({ contact, lastMsg, unread, mentionCount=0, active, onClick, isGroup }) {
  const lastText = lastMsg?.callType ? `${lastMsg.callType==="video"?"📹":"📞"} Call`
    : lastMsg?.type==="voice" ? "🎤 Voice message"
    : lastMsg?.type==="image" ? "📷 Photo"
    : lastMsg?.type==="file"  ? `📎 ${lastMsg.fileName||"File"}`
    : stripMentionTags(lastMsg?.text) || "";
  return (
    <div className="sidebar-item" onClick={onClick}
      style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", cursor:"pointer", background:active?T.blue50:"transparent", borderLeft:active?`3px solid ${T.blue500}`:"3px solid transparent" }}>
      {isGroup ? <Avatar group size={46} /> : <Avatar user={contact} size={46} online={contact.online} />}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8 }}>
          <span style={{ fontWeight:600, fontSize:14, color:active?T.blue700:T.slate800, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{contact.name}</span>
          {lastMsg && <span style={{ fontSize:10.5, color:T.slate400, flexShrink:0 }}>{lastMsg.t}</span>}
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:2 }}>
          <span style={{ fontSize:12.5, color:T.slate500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lastText||(isGroup?"Group chat":contact.role)}</span>
          <div style={{ display:"flex", gap:5, alignItems:"center", flexShrink:0 }}>
            {!isGroup && (
              <span style={{ fontSize:10, fontWeight:600, borderRadius:6, padding:"2px 7px", color:(ROLE_CONFIG[contact.role]||ROLE_CONFIG.Other).color, background:(ROLE_CONFIG[contact.role]||ROLE_CONFIG.Other).bg }}>{contact.role}</span>
            )}
            {mentionCount>0 && (
              <span title="You were privately @mentioned" style={{ display:"flex", alignItems:"center", gap:3, background:T.violet, color:"#fff", borderRadius:10, fontSize:10.5, fontWeight:700, padding:"2px 7px" }}>
                @{mentionCount>1?mentionCount:""}
              </span>
            )}
            {unread>0 && <span style={{ background:T.blue500, color:"#fff", borderRadius:"50%", fontSize:11, fontWeight:700, minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>{unread}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MODALS
══════════════════════════════════════════════════════════ */
function ModalWrap({ title, onClose, children, width=420 }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:8000, background:"rgba(15,23,42,0.4)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:T.white, borderRadius:20, padding:"28px 28px", width:"100%", maxWidth:width, boxShadow:"0 24px 60px rgba(0,0,0,0.14)", animation:"slideUp .2s ease" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div style={{ fontWeight:700, fontSize:20, color:T.slate900, fontFamily:"'Instrument Serif',serif" }}>{title}</div>
          <button onClick={onClose} style={{ background:T.slate100, border:"none", borderRadius:8, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.slate500 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
const FLabel = ({children}) => <div style={{ fontSize:11.5, fontWeight:600, color:T.slate500, marginBottom:6, textTransform:"uppercase", letterSpacing:0.8 }}>{children}</div>;
const FInput = ({value,onChange,placeholder,autoFocus,type="text",onEnter}) => (
  <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus}
    onKeyDown={e=>{ if (e.key==="Enter" && onEnter) onEnter(); }}
    style={{ width:"100%", padding:"10px 13px", borderRadius:10, border:`1.5px solid ${T.slate200}`, fontSize:14, color:T.slate800, marginBottom:16, outline:"none", background:T.slate50 }}
    onFocus={e=>e.target.style.borderColor=T.blue500}
    onBlur={e=>e.target.style.borderColor=T.slate200} />
);
const RolePicker = ({roles,selected,onSelect}) => (
  <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:16 }}>
    {roles.map(r=>{ const rc=ROLE_CONFIG[r]||ROLE_CONFIG.Other; return <button key={r} onClick={()=>onSelect(r)} style={{ padding:"5px 13px", borderRadius:50, border:`1.5px solid ${selected===r?rc.color:T.slate200}`, background:selected===r?rc.bg:T.white, color:selected===r?rc.color:T.slate500, fontWeight:600, fontSize:12.5 }}>{r}</button>; })}
  </div>
);
const ErrMsg    = ({children}) => <div style={{ color:T.danger, fontSize:12.5, marginBottom:10 }}>{children}</div>;
const PrimaryBtn = ({onClick,children,full,disabled}) => (
  <button onClick={onClick} disabled={disabled} style={{ width:full?"100%":"auto", flex:full?undefined:1, padding:"11px 20px", borderRadius:10, border:"none", background:disabled?T.slate300:T.blue600, color:"#fff", fontWeight:700, fontSize:14, boxShadow:disabled?"none":`0 4px 14px rgba(37,99,235,.3)` }}>{children}</button>
);
const GhostBtn = ({onClick,children}) => (
  <button onClick={onClick} style={{ flex:1, padding:"11px 20px", borderRadius:10, border:`1.5px solid ${T.slate200}`, background:T.white, color:T.slate600, fontWeight:600, fontSize:14 }}>{children}</button>
);

function AddContactModal({ onSave, onClose }) {
  const [term, setTerm]       = useState("");
  const [results, setResults] = useState([]);
  const [busyId, setBusyId]   = useState(null);
  const [err, setErr]         = useState("");
  const [searching, setSearching] = useState(false);

  // Search registered users by name or ID as you type (anyone can be found — no role limits).
  useEffect(() => {
    const t = term.trim();
    if (t.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const h = setTimeout(async () => {
      try { const { users } = await api(`/api/users/search?q=${encodeURIComponent(t)}`); setResults(users); setErr(""); }
      catch (e) { setErr(e.message); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(h);
  }, [term]);

  const save = async u => {
    setBusyId(u.id); setErr("");
    const res = await onSave({ id:u.id, name:"" });
    setBusyId(null);
    if (res?.error) { setErr(res.error); return; }
    setResults(r => r.map(x => x.id===u.id ? { ...x, saved:true } : x));
  };
  return (
    <ModalWrap title="Add New Contact" onClose={onClose}>
      <FLabel>Search by name or ID</FLabel>
      <FInput placeholder="e.g. Priya or NU002" value={term} onChange={setTerm} autoFocus />
      <div style={{ maxHeight:260, overflowY:"auto", border:`1px solid ${T.slate200}`, borderRadius:12, marginBottom:14 }}>
        {term.trim().length<2 && <div style={{ padding:16, fontSize:13, color:T.slate400, textAlign:"center" }}>Type at least 2 letters of their name or ID.</div>}
        {term.trim().length>=2 && !searching && results.length===0 && <div style={{ padding:16, fontSize:13, color:T.slate400, textAlign:"center" }}>No registered user found. Ask them to create an account first.</div>}
        {searching && <div style={{ padding:16, fontSize:13, color:T.slate400, textAlign:"center" }}>Searching…</div>}
        {results.map(u => (
          <div key={u.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 14px", borderBottom:`1px solid ${T.slate100}` }}>
            <Avatar user={u} size={36} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13.5, fontWeight:600, color:T.slate800 }}>{u.name}</div>
              <div style={{ fontSize:11.5, color:T.slate400 }}>{u.role} · {u.id}</div>
            </div>
            {u.saved
              ? <span style={{ fontSize:12, color:T.slate400, fontWeight:600 }}>✓ Saved</span>
              : <button onClick={()=>save(u)} disabled={busyId===u.id}
                  style={{ padding:"6px 14px", borderRadius:8, border:"none", background:T.blue600, color:"#fff", fontSize:12.5, fontWeight:700 }}>{busyId===u.id?"…":"Save"}</button>}
          </div>
        ))}
      </div>
      {err && <ErrMsg>{err}</ErrMsg>}
      <GhostBtn onClick={onClose} full>Done</GhostBtn>
    </ModalWrap>
  );
}

function EditContactModal({ contact, onSave, onClose }) {
  const [name, setName] = useState(contact.name||"");
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { setErr("Name required"); return; }
    setBusy(true);
    const res = await onSave({...contact,name:name.trim()});
    setBusy(false);
    if (res?.error) { setErr(res.error); return; }
    onClose();
  };
  return (
    <ModalWrap title="Edit Contact" onClose={onClose}>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}><Avatar user={{...contact,name}} size={60} /></div>
      <FLabel>Saved name</FLabel><FInput placeholder="Full name" value={name} onChange={setName} onEnter={save} />
      <div style={{ background:T.slate50, borderRadius:10, padding:"8px 12px", fontSize:12.5, color:T.slate500, marginBottom:16 }}>
        ID <strong style={{ color:T.blue600 }}>{contact.id}</strong> · {contact.role}{contact.realName && contact.realName!==name.trim() ? <> · registered as <strong>{contact.realName}</strong></> : null}
      </div>
      {err && <ErrMsg>{err}</ErrMsg>}
      <div style={{ display:"flex", gap:10 }}><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={save} disabled={busy}>Save Changes</PrimaryBtn></div>
    </ModalWrap>
  );
}

function CreateGroupModal({ contacts, me, onSave, onClose }) {
  const [name, setName]       = useState("");
  const [selected, setSelected] = useState([]);
  const [err, setErr]         = useState("");
  const toggle = id => setSelected(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim())        { setErr("Group name required"); return; }
    if (selected.length<1)   { setErr("Select at least 1 member"); return; }
    setBusy(true); setErr("");
    const res = await onSave({ name:name.trim(), memberIds:selected });
    setBusy(false);
    if (res?.error) { setErr(res.error); return; }
    onClose();
  };
  return (
    <ModalWrap title="Create Group Chat" onClose={onClose}>
      <FLabel>Group Name</FLabel><FInput placeholder="e.g. ICU Team" value={name} onChange={setName} autoFocus />
      <FLabel>Select Members</FLabel>
      <div style={{ maxHeight:220, overflowY:"auto", border:`1px solid ${T.slate200}`, borderRadius:12, marginBottom:14 }}>
        {contacts.length===0 && <div style={{ padding:16, fontSize:13, color:T.slate400, textAlign:"center" }}>Save some contacts first (➕ at the top), then add them here.</div>}
        {contacts.map(c=>(
          <div key={c.id} onClick={()=>toggle(c.id)}
            style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 14px", cursor:"pointer", background:selected.includes(c.id)?T.blue50:T.white, borderBottom:`1px solid ${T.slate100}` }}>
            <Avatar user={c} size={36} />
            <div style={{ flex:1 }}><div style={{ fontSize:13.5, fontWeight:600, color:T.slate800 }}>{c.name}</div><div style={{ fontSize:11.5, color:T.slate400 }}>{c.role} · {c.id}</div></div>
            <div style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${selected.includes(c.id)?T.blue500:T.slate300}`, background:selected.includes(c.id)?T.blue500:"transparent", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:12 }}>
              {selected.includes(c.id) && "✓"}
            </div>
          </div>
        ))}
      </div>
      {err && <ErrMsg>{err}</ErrMsg>}
      <div style={{ display:"flex", gap:10 }}><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={save} disabled={busy}>{busy?"Creating…":`Create Group (${selected.length+1})`}</PrimaryBtn></div>
    </ModalWrap>
  );
}

// Regex used to find links inside a text message for the "Links" tab.
// Two copies on purpose: HAS_URL (no /g) is safe to reuse across repeated
// .test() calls in a .filter() loop; URL_RE (/g) is only ever used with
// .match(), which doesn't carry state between calls the way .test() does.
const HAS_URL = /(https?:\/\/[^\s]+|www\.[^\s]+)/i;
const URL_RE  = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

function GroupInfoModal({ group, members, contacts, me, messages, onAddMember, onClose, onViewMedia }) {
  const [view, setView] = useState("members"); // "members" | "addMember" | "media"
  const addable = contacts.filter(c => !(group.memberIds||[]).includes(c.id));

  const mediaMsgs = useMemo(() => (messages||[]).filter(m => m.type==="image" || isVideoFile(m)), [messages]);
  const linkMsgs  = useMemo(() => (messages||[]).filter(m => m.type==="text" && HAS_URL.test(m.text||"")), [messages]);
  const docMsgs   = useMemo(() => (messages||[]).filter(m => m.type==="file" && !isVideoFile(m)), [messages]);

  const title = view==="addMember" ? "Add Member" : view==="media" ? "Media, links and docs" : group.name;

  return (
    <ModalWrap title={title} onClose={onClose}>
      {view==="members" && (
        <>
          <div style={{ fontSize:12, color:T.slate400, marginBottom:14 }}>{members.length} members</div>
          <div style={{ maxHeight:280, overflowY:"auto", border:`1px solid ${T.slate200}`, borderRadius:12, marginBottom:16 }}>
            {members.map(m=>(
              <div key={m.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", borderBottom:`1px solid ${T.slate100}` }}>
                <Avatar user={m} size={38} online={m.id===me.id?true:m.online} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13.5, fontWeight:600, color:T.slate800 }}>{m.name}{m.id===me.id?" (You)":""}</div>
                  <div style={{ fontSize:11.5, color:T.slate400 }}>{m.role} · {m.id}</div>
                </div>
              </div>
            ))}
          </div>
          {/* WhatsApp-style entry point: shows a live count and opens the
              Gallery / Links / Documents tabs for everything shared here. */}
          <div onClick={()=>setView("media")}
            style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", cursor:"pointer", border:`1px solid ${T.slate200}`, borderRadius:12, marginBottom:12 }}
            onMouseEnter={e=>e.currentTarget.style.background=T.slate50}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div style={{ width:36, height:36, borderRadius:10, background:T.blue50, color:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>🖼️</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13.5, fontWeight:600, color:T.slate800 }}>Media, links and docs</div>
              <div style={{ fontSize:11.5, color:T.slate400 }}>{mediaMsgs.length + linkMsgs.length + docMsgs.length} items</div>
            </div>
            <span style={{ color:T.slate400, fontSize:16 }}>›</span>
          </div>
          <PrimaryBtn onClick={()=>setView("addMember")} full>➕ Add Member</PrimaryBtn>
        </>
      )}

      {view==="addMember" && (
        <>
          <div style={{ maxHeight:280, overflowY:"auto", border:`1px solid ${T.slate200}`, borderRadius:12, marginBottom:16 }}>
            {addable.length===0 && <div style={{ padding:16, fontSize:13, color:T.slate400, textAlign:"center" }}>All your contacts are already in this group.</div>}
            {addable.map(c=>(
              <div key={c.id} onClick={()=>{ onAddMember(c.id); setView("members"); }}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", cursor:"pointer", borderBottom:`1px solid ${T.slate100}` }}
                onMouseEnter={e=>e.currentTarget.style.background=T.slate50}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <Avatar user={c} size={36} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13.5, fontWeight:600, color:T.slate800 }}>{c.name}</div>
                  <div style={{ fontSize:11.5, color:T.slate400 }}>{c.role} · {c.id}</div>
                </div>
                <span style={{ color:T.blue500, fontSize:18 }}>+</span>
              </div>
            ))}
          </div>
          <GhostBtn onClick={()=>setView("members")} full>← Back</GhostBtn>
        </>
      )}

      {view==="media" && (
        <MediaLinksTabs mediaMsgs={mediaMsgs} linkMsgs={linkMsgs} docMsgs={docMsgs}
          onOpenMedia={id=>{ onViewMedia?.(id); onClose(); }}
          onBack={()=>setView("members")} />
      )}
    </ModalWrap>
  );
}

// Gallery / Links / Documents tabs — shows every image, link, and file ever
// sent in this chat, WhatsApp-style, so nothing has to be scrolled back to
// find later.
function MediaLinksTabs({ mediaMsgs, linkMsgs, docMsgs, onOpenMedia, onBack }) {
  const [tab, setTab] = useState("gallery");
  const tabs = [
    { key:"gallery",   label:`Gallery (${mediaMsgs.length})` },
    { key:"links",     label:`Links (${linkMsgs.length})` },
    { key:"documents", label:`Docs (${docMsgs.length})` },
  ];
  const downloadFile = (msg) => {
    try {
      const src = msg.fileUrl;
      if (src && src.startsWith("data:")) {
        const arr = src.split(","); const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]); let n = bstr.length; const u8 = new Uint8Array(n);
        while(n--) u8[n]=bstr.charCodeAt(n);
        const blob = new Blob([u8],{type:mime});
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = blobUrl; a.download = msg.fileName || "file";
        document.body.appendChild(a); a.click();
        setTimeout(()=>{ document.body.removeChild(a); URL.revokeObjectURL(blobUrl); },2000);
      } else if (src) {
        // big files are streamed straight to disk by the browser (not loaded into memory)
        const a = document.createElement("a"); a.href = mediaSrc(src,{download:true}); a.download = msg.fileName || "file";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
    } catch(e) { alert("Could not download: "+e.message); }
  };
  return (
    <>
      <div style={{ display:"flex", gap:6, marginBottom:14, background:T.slate100, borderRadius:10, padding:4 }}>
        {tabs.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{ flex:1, padding:"7px 6px", borderRadius:8, border:"none", fontSize:12, fontWeight:700, cursor:"pointer",
              background:tab===t.key?T.white:"transparent", color:tab===t.key?T.blue600:T.slate500,
              boxShadow:tab===t.key?"0 1px 3px rgba(0,0,0,0.08)":"none" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==="gallery" && (
        mediaMsgs.length===0 ? <EmptyMediaState icon="🖼️" text="No photos or videos yet" /> : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6, maxHeight:320, overflowY:"auto" }}>
            {mediaMsgs.map(m=>(
              <div key={m.id} onClick={()=>onOpenMedia(m.id)}
                style={{ position:"relative", paddingTop:"100%", borderRadius:10, overflow:"hidden", cursor:"pointer", background:T.slate100 }}>
                {m.type==="image" ? (
                  <img src={mediaSrc(m.imageUrl)} alt="" style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
                ) : (
                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>🎬</div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {tab==="links" && (
        linkMsgs.length===0 ? <EmptyMediaState icon="🔗" text="No links shared yet" /> : (
          <div style={{ maxHeight:320, overflowY:"auto", display:"flex", flexDirection:"column", gap:8 }}>
            {linkMsgs.map(m=>{
              const url = (m.text.match(URL_RE)||[])[0];
              const href = url && (url.startsWith("http") ? url : `https://${url}`);
              return (
                <a key={m.id} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ display:"block", padding:"10px 12px", border:`1px solid ${T.slate200}`, borderRadius:10, textDecoration:"none" }}>
                  <div style={{ fontSize:12.5, fontWeight:600, color:T.blue600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{url}</div>
                  <div style={{ fontSize:11.5, color:T.slate400, marginTop:2 }}>{m.t}</div>
                </a>
              );
            })}
          </div>
        )
      )}

      {tab==="documents" && (
        docMsgs.length===0 ? <EmptyMediaState icon="📎" text="No documents shared yet" /> : (
          <div style={{ maxHeight:320, overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
            {docMsgs.map(m=>(
              <div key={m.id} onClick={()=>downloadFile(m)}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 12px", border:`1px solid ${T.slate200}`, borderRadius:10, cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background=T.slate50}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{ width:34, height:34, borderRadius:9, background:T.blue50, color:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>📎</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.slate800, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.fileName||"Document"}</div>
                  <div style={{ fontSize:11, color:T.slate400 }}>{m.fileSize||""} {m.t?`· ${m.t}`:""}</div>
                </div>
                <span style={{ color:T.blue500, fontSize:14 }}>⬇</span>
              </div>
            ))}
          </div>
        )
      )}

      <GhostBtn onClick={onBack} full>← Back</GhostBtn>
    </>
  );
}
const EmptyMediaState = ({ icon, text }) => (
  <div style={{ padding:"40px 0", textAlign:"center", color:T.slate400, fontSize:13 }}>
    <div style={{ fontSize:30, marginBottom:8 }}>{icon}</div>{text}
  </div>
);

// Same "Media, links and docs" view as GroupInfoModal, but for a regular
// 1:1 chat — there's no member list here, so it goes straight to the tabs.
function ContactMediaModal({ contact, messages, onClose, onViewMedia }) {
  const mediaMsgs = useMemo(() => (messages||[]).filter(m => m.type==="image" || isVideoFile(m)), [messages]);
  const linkMsgs  = useMemo(() => (messages||[]).filter(m => m.type==="text" && HAS_URL.test(m.text||"")), [messages]);
  const docMsgs   = useMemo(() => (messages||[]).filter(m => m.type==="file" && !isVideoFile(m)), [messages]);
  return (
    <ModalWrap title={`${contact.name} · Media`} onClose={onClose}>
      <MediaLinksTabs mediaMsgs={mediaMsgs} linkMsgs={linkMsgs} docMsgs={docMsgs}
        onOpenMedia={id=>{ onViewMedia?.(id); onClose(); }}
        onBack={onClose} />
    </ModalWrap>
  );
}

function EditModal({ msg, onSave, onClose }) {
  const [text, setText] = useState(msg.text||"");
  return (
    <ModalWrap title="Edit Message" onClose={onClose} width={380}>
      <textarea value={text} onChange={e=>setText(e.target.value)}
        style={{ width:"100%", padding:"10px 13px", borderRadius:10, border:`1.5px solid ${T.slate200}`, fontSize:14, color:T.slate800, resize:"vertical", minHeight:80, outline:"none", background:T.slate50, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
        onFocus={e=>e.target.style.borderColor=T.blue500}
        onBlur={e=>e.target.style.borderColor=T.slate200} />
      <div style={{ display:"flex", gap:10, marginTop:14 }}><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={()=>{ onSave(text.trim()); onClose(); }}>Save</PrimaryBtn></div>
    </ModalWrap>
  );
}

function ForwardModal({ msg, contacts, groups, me, onForward, onClose }) {
  const [selected, setSelected] = useState(null);
  const all = [...contacts.map(c=>({...c,isGroup:false})),...groups.map(g=>({...g,isGroup:true}))];
  return (
    <ModalWrap title="Forward Message" onClose={onClose} width={380}>
      <div style={{ maxHeight:260, overflowY:"auto", border:`1px solid ${T.slate200}`, borderRadius:12, marginBottom:14 }}>
        {all.map(c=>(
          <div key={c.id} onClick={()=>setSelected(c.id)}
            style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 14px", cursor:"pointer", background:selected===c.id?T.blue50:T.white, borderBottom:`1px solid ${T.slate100}` }}>
            {c.isGroup ? <Avatar group size={36} /> : <Avatar user={c} size={36} />}
            <div><div style={{ fontSize:13.5, fontWeight:600, color:T.slate800 }}>{c.name}</div>{!c.isGroup&&<div style={{ fontSize:11.5, color:T.slate400 }}>{c.role}</div>}</div>
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:10 }}><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={()=>{ if(selected){ onForward(selected); onClose(); } }} disabled={!selected}>Forward</PrimaryBtn></div>
    </ModalWrap>
  );
}

function StarredPanel({ starred, allConvs, contacts, me, onClose }) {
  const items = [];
  Object.entries(allConvs).forEach(([key,msgs]) => {
    msgs.forEach(m => {
      if (starred[m.id]) {
        const ids = key.split("|");
        const otherId = ids.find(x=>x!==me.id);
        const contact = contacts.find(c=>c.id===otherId);
        items.push({ msg:m, chatName:contact?.name||key });
      }
    });
  });
  return (
    <ModalWrap title="⭐ Starred Messages" onClose={onClose}>
      <div style={{ overflowY:"auto", maxHeight:380 }}>
        {items.length===0 && <div style={{ color:T.slate400, textAlign:"center", padding:"40px 0", fontSize:14 }}>No starred messages yet</div>}
        {items.map(({msg,chatName})=>(
          <div key={msg.id} style={{ padding:"10px 12px", borderBottom:`1px solid ${T.slate100}`, borderRadius:10, marginBottom:4 }}>
            <div style={{ fontSize:11.5, color:T.blue600, fontWeight:600, marginBottom:3 }}>{chatName}</div>
            <div style={{ fontSize:13.5, color:T.slate700 }}>{stripMentionTags(msg.text)||(msg.type==="voice"?"🎤 Voice":msg.type==="image"?"📷 Image":"📎 File")}</div>
            <div style={{ fontSize:11, color:T.slate400, marginTop:3 }}>{msg.t}</div>
          </div>
        ))}
      </div>
    </ModalWrap>
  );
}

// Chrome's MediaRecorder-produced .webm files frequently don't have a valid
// duration written into their header (shows 0:00 and won't play/seek
// properly). The standard fix: seek far past the end once metadata loads,
// which forces the browser to scan the file and recompute the real
// duration, then seek back to the start — after that it plays normally.
function fixRecordedMediaDuration(e) {
  const el = e.target;
  if (el.duration === Infinity || isNaN(el.duration) || el.duration === 0) {
    el.currentTime = 1e101;
    const onTimeUpdate = () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.currentTime = 0;
    };
    el.addEventListener("timeupdate", onTimeUpdate);
  }
}

function CallHistoryModal({ recordings, onClose }) {
  const ordered = [...recordings].reverse(); // most recent recording first
  return (
    <ModalWrap title="📼 Call History" onClose={onClose} width={460}>
      <div style={{ overflowY:"auto", maxHeight:440 }}>
        {ordered.length===0 && <div style={{ color:T.slate400, textAlign:"center", padding:"40px 0", fontSize:14 }}>No recorded calls yet</div>}
        {ordered.map(rec => (
          <div key={rec.id} style={{ padding:"12px", borderBottom:`1px solid ${T.slate100}`, borderRadius:10, marginBottom:8, background:T.slate50 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:16 }}>{rec.callType==="video" ? "📹" : "📞"}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13.5, fontWeight:700, color:T.slate800, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {rec.isGroup ? `${rec.groupName||"Group call"} (${(rec.participants||[]).join(", ")})` : (rec.peerName||rec.peerId||"Unknown")}
                </div>
                <div style={{ fontSize:11, color:T.slate400 }}>{rec.t}</div>
              </div>
            </div>
            {rec.isVideo
              ? <video src={rec.recordingUrl} controls onLoadedMetadata={fixRecordedMediaDuration} style={{ width:"100%", borderRadius:8, background:"#000", maxHeight:220 }} />
              : <audio src={rec.recordingUrl} controls onLoadedMetadata={fixRecordedMediaDuration} style={{ width:"100%" }} />}
          </div>
        ))}
      </div>
    </ModalWrap>
  );
}

/* ══════════════════════════════════════════════════════════
   CREATE PROFILE SCREEN
══════════════════════════════════════════════════════════ */
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ name:"", role:"Member", id:"", password:"", photo:null });
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(false);
  const photoRef = useRef();
  const roles = ["Member"];
  const isReg = mode==="register";
  const submit = async () => {
    if (isReg && !form.name.trim()) { setErr("Please enter your name"); return; }
    if (!form.id.trim())            { setErr("Please enter your ID"); return; }
    if (!form.password)             { setErr("Please enter your password"); return; }
    if (isReg && form.password.length<6) { setErr("Password must be at least 6 characters"); return; }
    setBusy(true); setErr("");
    try {
      const data = isReg
        ? await api("/api/register", { method:"POST", body:{ id:form.id.trim(), name:form.name.trim(), role:form.role, password:form.password, photo:form.photo||null } })
        : await api("/api/login",    { method:"POST", body:{ id:form.id.trim(), password:form.password } });
      setToken(data.token);
      onAuth(data.user);
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const pickPhoto = async e => {
    const file = e.target.files?.[0]; e.target.value="";
    if (!file) return;
    try { const photo = await readAndResizeImage(file); setForm(f=>({...f, photo})); }
    catch { alert("Could not use that image, please try another."); }
  };
  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:`linear-gradient(135deg,${T.blue50} 0%,${T.slate100} 50%,${T.slate50} 100%)`, padding:20 }}>
      <div style={{ background:T.white, borderRadius:24, padding:"36px 40px", width:"100%", maxWidth:440, boxShadow:"0 25px 60px rgba(0,0,0,0.1)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:8 }}>
          <div style={{ width:48, height:48, borderRadius:14, background:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, boxShadow:`0 4px 14px rgba(37,99,235,0.35)` }}>💬</div>
          <div>
            <div style={{ fontSize:26, fontWeight:700, color:T.slate900, fontFamily:"'Instrument Serif',serif" }}>IDK</div>
            <div style={{ fontSize:11.5, color:T.slate400, letterSpacing:1.5, textTransform:"uppercase", fontWeight:600 }}>Simple, private messaging</div>
          </div>
        </div>
        <div style={{ height:1, background:T.slate100, margin:"18px 0" }} />
        <div style={{ display:"flex", background:T.slate100, borderRadius:12, padding:4, marginBottom:20 }}>
          {[["login","Log in"],["register","Create account"]].map(([m,label])=>(
            <button key={m} onClick={()=>{ setMode(m); setErr(""); }}
              style={{ flex:1, padding:"8px 0", border:"none", borderRadius:9, fontWeight:700, fontSize:13, background:mode===m?T.white:"transparent", color:mode===m?T.blue600:T.slate500, boxShadow:mode===m?"0 1px 4px rgba(0,0,0,.08)":"none" }}>{label}</button>
          ))}
        </div>
        <div style={{ background:T.blue50, border:`1px solid ${T.blue200}`, borderRadius:12, padding:"10px 14px", fontSize:12.5, color:T.blue700, marginBottom:20, display:"flex", gap:8 }}>
          <span style={{ flexShrink:0 }}>🔒</span>
          <span>Chat with people, share files, and stay connected.</span>
        </div>
        {isReg && (
          <div style={{ display:"flex", justifyContent:"center", marginBottom:18 }}>
            <input ref={photoRef} type="file" accept="image/*" style={{ display:"none" }} onChange={pickPhoto} />
            <div onClick={()=>photoRef.current?.click()} title="Add profile photo" style={{ position:"relative", cursor:"pointer" }}>
              <Avatar user={{...form}} size={72} />
              <div style={{ position:"absolute", bottom:-2, right:-2, width:26, height:26, borderRadius:"50%", background:T.blue600, border:`2px solid ${T.white}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:"#fff" }}>📷</div>
            </div>
          </div>
        )}
        {isReg && <>
          <FLabel>Your Full Name</FLabel>
          <FInput placeholder="e.g. Alex Morgan" value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} autoFocus />
        </>}
        <FLabel>{isReg ? "Choose your username or ID" : "Your ID"}</FLabel>
        <FInput placeholder="e.g. alex123" value={form.id} onChange={v=>setForm(f=>({...f,id:v}))} autoFocus={!isReg} onEnter={submit} />
        <FLabel>Password</FLabel>
        <FInput type="password" placeholder={isReg?"At least 6 characters":"Your password"} value={form.password} onChange={v=>setForm(f=>({...f,password:v}))} onEnter={submit} />
        {err && <ErrMsg>{err}</ErrMsg>}
        <PrimaryBtn onClick={submit} full disabled={busy}>{busy ? "Please wait…" : isReg ? "Create account →" : "Log in →"}</PrimaryBtn>
        <div style={{ marginTop:12, fontSize:12, color:T.slate400, textAlign:"center" }}>
          {isReg ? "Others can find you by your ID and message you directly." : "New here? Choose “Create account” above."}
        </div>
      </div>
    </div>
  );
}

function HdrBtn({ children, onClick, title }) {
  return (
    <button onClick={onClick} title={title}
      style={{ background:"none", border:"none", width:34, height:34, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:T.slate500, flexShrink:0 }}
      onMouseEnter={e=>{ e.currentTarget.style.background=T.blue50; e.currentTarget.style.color=T.blue600; }}
      onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=T.slate500; }}>
      {children}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════════ */
const EXTERNAL_APPS = [
  { id:"whatsapp", name:"WhatsApp Web", short:"WhatsApp", url:"https://web.whatsapp.com/", emoji:"🟢", note:"Sign in through WhatsApp's official web flow." },
  { id:"instagram", name:"Instagram", short:"Instagram", url:"https://www.instagram.com/", emoji:"📸", note:"Use Instagram's official website login." },
  { id:"facebook", name:"Facebook Messenger", short:"Messenger", url:"https://www.messenger.com/", emoji:"💬", note:"Messenger may redirect to Facebook login." },
  { id:"telegram", name:"Telegram Web", short:"Telegram", url:"https://web.telegram.org/", emoji:"✈️", note:"Use Telegram's official web login." },
];

function ExternalAppsHub({ onClose }) {
  const [tabs, setTabs] = useState([{ id:"home", title:"Apps", url:"", emoji:"🌐" }]);
  const [activeId, setActiveId] = useState("home");
  const [address, setAddress] = useState("");
  const active = tabs.find(t => t.id === activeId) || tabs[0];
  const openApp = app => {
    if (!tabs.some(t => t.id === app.id)) setTabs(prev => [...prev, { id:app.id, title:app.short, url:app.url, emoji:app.emoji }]);
    setActiveId(app.id); setAddress(app.url);
  };
  const openAddress = value => {
    const raw = (value || address).trim(); if (!raw) return;
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    let host = "Website"; try { host = new URL(url).hostname.replace(/^www\./,""); } catch {}
    const id = `site-${Date.now()}`;
    setTabs(prev => [...prev, { id, title:host, url, emoji:"🌐" }]); setActiveId(id); setAddress(url);
  };
  const openExternal = url => window.open(url, "_blank", "noopener,noreferrer");
  const closeTab = (id,e) => { e.stopPropagation(); const next=tabs.filter(t=>t.id!==id); setTabs(next); if(activeId===id)setActiveId(next[next.length-1]?.id||"home"); };
  return <div style={{position:"fixed",inset:0,zIndex:5000,background:"#f5f7fb",color:"#172033",fontFamily:"'Plus Jakarta Sans',sans-serif",display:"flex",flexDirection:"column"}}>
    <div style={{minHeight:58,display:"flex",alignItems:"center",gap:9,padding:"8px 12px",background:"#fff",borderBottom:"1px solid #e3e8f0",flexWrap:"wrap"}}>
      <button onClick={onClose} style={{border:0,borderRadius:9,padding:"9px 12px",background:"#2563eb",color:"white",fontWeight:700,cursor:"pointer"}}>← IDK</button>
      <strong>App Browser</strong>
      <div style={{display:"flex",gap:5,flex:1,overflowX:"auto",minWidth:120}}>{tabs.map(tab=><button key={tab.id} onClick={()=>{setActiveId(tab.id);setAddress(tab.url)}} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 10px",borderRadius:9,border:"1px solid "+(activeId===tab.id?"#bfdbfe":"#e5e7eb"),background:activeId===tab.id?"#eff6ff":"white",whiteSpace:"nowrap",cursor:"pointer",fontSize:12}}>{tab.emoji} {tab.title}{tab.id!=="home"&&<span onClick={e=>closeTab(tab.id,e)}> ×</span>}</button>)}</div>
    </div>
    {active.id!=="home"&&<form onSubmit={e=>{e.preventDefault();openAddress(address)}} style={{display:"flex",gap:8,padding:10,background:"white",borderBottom:"1px solid #e3e8f0"}}><input value={address} onChange={e=>setAddress(e.target.value)} placeholder="Website address" style={{flex:1,minWidth:0,border:"1px solid #dbe2ea",borderRadius:9,padding:9}}/><button type="submit" style={{border:0,borderRadius:9,padding:"9px 14px",background:"#2563eb",color:"white",fontWeight:700}}>Go</button><button type="button" onClick={()=>openExternal(active.url)} style={{border:"1px solid #dbe2ea",borderRadius:9,padding:"8px 10px",background:"white",whiteSpace:"nowrap"}}>Open in browser ↗</button></form>}
    {active.id==="home"?<div style={{flex:1,overflowY:"auto",padding:"clamp(20px,5vw,55px)",maxWidth:1100,width:"100%",boxSizing:"border-box",margin:"0 auto"}}>
      <div style={{maxWidth:700,margin:"0 auto 28px"}}><div style={{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:"#64748b",fontWeight:800,marginBottom:10}}>One place for your apps</div><div style={{fontSize:"clamp(26px,4vw,38px)",fontWeight:800,marginBottom:10}}>Your messaging, your tabs.</div><div style={{color:"#64748b",lineHeight:1.65}}>Open a service and sign in on its official website. If it blocks embedding, use “Open in browser”.</div><form onSubmit={e=>{e.preventDefault();openAddress(address)}} style={{display:"flex",gap:8,marginTop:20}}><input value={address} onChange={e=>setAddress(e.target.value)} placeholder="Enter website, e.g. example.com" style={{flex:1,minWidth:0,border:"1px solid #dbe2ea",borderRadius:12,padding:13}}/><button type="submit" style={{border:0,borderRadius:12,padding:"12px 18px",background:"#2563eb",color:"white",fontWeight:800}}>Open</button></form></div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:14}}>{EXTERNAL_APPS.map(app=><button key={app.id} onClick={()=>openApp(app)} style={{textAlign:"left",border:"1px solid #e1e7ef",background:"white",borderRadius:18,padding:20,cursor:"pointer"}}><div style={{fontSize:28,marginBottom:12}}>{app.emoji}</div><div style={{fontSize:16,fontWeight:800,marginBottom:6}}>{app.name}</div><div style={{color:"#64748b",fontSize:12,lineHeight:1.6}}>{app.note}</div><div style={{color:"#2563eb",fontSize:12,fontWeight:800,marginTop:14}}>Open tab →</div></button>)}</div>
      <div style={{marginTop:22,padding:14,borderRadius:12,background:"#fff7ed",color:"#9a3412",fontSize:12,lineHeight:1.6,border:"1px solid #fed7aa"}}><strong>Note:</strong> Some services block display inside another website. Sign in only on official pages; this app does not collect your external passwords. The browser fallback opens the service separately.</div>
    </div>:<div style={{flex:1,minHeight:0,position:"relative",background:"white"}}><iframe key={active.id} title={active.title} src={active.url} referrerPolicy="strict-origin-when-cross-origin" style={{width:"100%",height:"100%",border:0,background:"white"}}/><div style={{position:"absolute",left:12,right:12,bottom:12,margin:"auto",maxWidth:620,background:"rgba(255,255,255,.97)",border:"1px solid #e2e8f0",boxShadow:"0 8px 30px rgba(15,23,42,.12)",borderRadius:14,padding:12,display:"flex",alignItems:"center",gap:12}}><div style={{flex:1,fontSize:12,lineHeight:1.5,color:"#475569"}}>If this page stays blank or shows a security error, the service blocks embedding. Open it in your browser instead.</div><button onClick={()=>openExternal(active.url)} style={{flexShrink:0,border:0,borderRadius:9,background:"#2563eb",color:"white",padding:"10px 12px",fontWeight:800,cursor:"pointer"}}>Open externally ↗</button></div></div>}
  </div>;
}

function Messenger({ me, setMe, onLogout }) {
  // Contacts and 1:1 chat history live in the DATABASE (loaded from the server).
  // Groups and their messages are in the database too.
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups]   = useState([]);
  const [convs, setConvs]     = useState({});
  const [hasMore, setHasMore] = useState({}); // chatKey -> are there older messages on the server?
  const [peer, setPeer]       = useState(null);
  const [filter, setFilter]   = useState("All");
  const [search, setSearch]   = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showGrp, setShowGrp] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showContactMedia, setShowContactMedia] = useState(false);
  const [viewMsgId, setViewMsgId] = useState(null);
  const [showStar, setShowStar] = useState(false);
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [showCam, setShowCam] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [unread, setUnread]   = useState({});
  const [mentionAlerts, setMentionAlerts] = useState({}); // groupKey -> count of unseen private @mentions addressed to me
  const [mentionToast, setMentionToast] = useState(null);  // transient "you were mentioned" banner
  const [starred, setStarred] = useState(() => LS.get(lsKey(me.id,"starred"), {}));
  const [replyTo, setReplyTo] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [editMsg, setEditMsg] = useState(null);
  const [fwdMsg, setFwdMsg]   = useState(null);
  const [editContact, setEditContact] = useState(null);
  const [pinnedMap, setPinnedMap] = useState(() => LS.get(lsKey(me.id,"pinned"), {}));
  const [call, setCall]       = useState({ visible:false });
  const [groupCallActive, setGroupCallActive] = useState({}); // groupId -> { type, startedBy }
  
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 769);
  const [appHubOpen, setAppHubOpen] = useState(false);
  const [translateSettings, setTranslateSettings] = useState(() => LS.get(lsKey(me.id,"trans"), {}));
  const [callRecordings, setCallRecordings] = useState([]);

  const ringingRef = useRef();
  const chatEnd    = useRef();
  const groupsRef   = useRef(groups);
  const contactsRef = useRef(contacts);
  const chatKeyRef  = useRef(null);
  const peerRef     = useRef(null);
  const stickToBottom = useRef(false);
  const onlineRef   = useRef({});
  useEffect(() => { groupsRef.current = groups; }, [groups]);
  useEffect(() => { contactsRef.current = contacts; }, [contacts]);

  const myPhotoRef = useRef();
  const changeMyPhoto = useCallback(async e => {
    const file = e.target.files?.[0]; e.target.value="";
    if (!file) return;
    try {
      const photo = await readAndResizeImage(file);
      setMe(m=>({...m, photo}));
      api("/api/me", { method:"PUT", body:{ photo } }).catch(()=>{}); // saved on the account → shows to contacts + survives re-login
    }
    catch { alert("Could not use that image, please try another."); }
  }, [setMe]);

  // ── Load saved data from the server ──────────────────────────
  const loadContacts = useCallback(async () => {
    try {
      const { contacts:list } = await api("/api/contacts");
      setContacts(prev => list.map(c => ({ ...c, online: !!onlineRef.current[c.id] || !!prev.find(p=>p.id===c.id&&p.online) })));
    } catch (e) { console.warn("Could not load contacts:", e.message); }
  }, []);

  // Sidebar: last message + unread count for every conversation.
  const loadConversations = useCallback(async () => {
    try {
      const { conversations } = await api("/api/conversations");
      setConvs(c => {
        const next = { ...c };
        conversations.forEach(({ key, last }) => {
          const existing = next[key] || [];
          if (!existing.some(m => m.id === last.id)) next[key] = [...existing, last];
        });
        return next;
      });
      setUnread(u => {
        const n = { ...u };
        conversations.forEach(({ key, unread:cnt }) => { n[key] = key === chatKeyRef.current ? 0 : cnt; });
        return n;
      });
    } catch (e) { console.warn("Could not load conversations:", e.message); }
  }, []);

  const keyOf = useCallback((p) => p.isGroup ? `group_${p.id}` : pairKey(me.id, p.id), [me.id]);

  // My groups (with members' registered names), last message + unread count.
  const loadGroups = useCallback(async () => {
    try {
      const { groups:list } = await api("/api/groups");
      setGroups(list.map(({ last, unread:_u, ...g }) => g));
      setConvs(c => {
        const next = { ...c };
        list.forEach(g => {
          const key = `group_${g.id}`;
          if (g.last && !(next[key]||[]).some(m => m.id === g.last.id)) next[key] = [...(next[key]||[]), g.last];
        });
        return next;
      });
      setUnread(u => {
        const n = { ...u };
        list.forEach(g => { const key = `group_${g.id}`; n[key] = key === chatKeyRef.current ? 0 : g.unread; });
        return n;
      });
    } catch (e) { console.warn("Could not load groups:", e.message); }
  }, []);

  // Full history of ONE chat (direct or group) — fetched from the database every time it is opened.
  const loadChat = useCallback(async (p) => {
    const key = keyOf(p);
    try {
      const { messages:server, hasMore:more } = await api(chatUrl(p));
      const lastTs = server.length ? server[server.length-1].ts : 0;
      stickToBottom.current = true; // history just (re)loaded → jump to the newest message
      setConvs(c => {
        const ids = new Set(server.map(m => m.id));
        // keep anything still sending / failed, or that arrived live after this snapshot
        const keep = (c[key]||[]).filter(m => !ids.has(m.id) && (m.pending || m.failed || (m.ts||0) > lastTs));
        return { ...c, [key]: [...server, ...keep] };
      });
      setHasMore(h => ({ ...h, [key]: more }));
    } catch (e) { console.warn("Could not load chat:", e.message); }
  }, [keyOf]);

  const loadOlder = useCallback(async (p) => {
    const key = keyOf(p);
    const first = (convs[key]||[]).find(m => m.ts);
    if (!first) return;
    try {
      const { messages:older, hasMore:more } = await api(`${chatUrl(p)}?before=${first.ts}`);
      setConvs(c => {
        const have = new Set((c[key]||[]).map(m => m.id));
        return { ...c, [key]: [...older.filter(m => !have.has(m.id)), ...(c[key]||[])] };
      });
      setHasMore(h => ({ ...h, [key]: more }));
    } catch (e) { console.warn("Could not load older messages:", e.message); }
  }, [keyOf, convs]);

  // Initial load, and a full re-sync every time the socket (re)connects — so anything
  // sent while this device was offline/asleep shows up straight away.
  useEffect(() => {
    const syncAll = () => {
      loadContacts(); loadConversations(); loadGroups();
      const p = peerRef.current;
      if (p) loadChat(p);
    };
    syncAll();
    socket.on("connect", syncAll);
    return () => socket.off("connect", syncAll);
  }, [loadContacts, loadConversations, loadGroups, loadChat]);

  // Join the Socket.IO room for every group we're a member of. Without
  // this, "group-message" broadcasts from the server never reach anyone
  // (the server only relays a group message to sockets that are IN that
  // room) — this was missing entirely, so group chat delivery was silently
  // broken for everyone except the sender's own optimistic local echo.
  //
  // Also re-runs on socket reconnect: a dropped/restored connection gets a
  // brand-new socket id server-side, so without re-joining here, delivery
  // would silently break again after any network blip.
  useEffect(() => {
    if (!me) return;
    const rejoinAll = () => {
      groups.forEach(g => socket.emit("join-group", g.id));
    };
    groups.forEach(g => socket.emit("join-group", g.id));
    socket.on("connect", rejoinAll);
    return () => socket.off("connect", rejoinAll);
  }, [me, groups]);

  useEffect(() => {
    if (!me) return;
    // A direct message addressed to ME, sent by another account. The server has already
    // saved it permanently; this just shows it live. `fromName` is the sender's account name.
    const onMsg = ({ fromName, fromRole, ...data }) => {
      const key = pairKey(me.id, data.from);
      // sender not saved yet → show them as a contact (like an unknown number on WhatsApp)
      setContacts(prev => prev.some(c=>c.id===data.from) ? prev
        : [...prev, { id:data.from, name:fromName||data.from, realName:fromName||null, alias:null, role:fromRole||"Other", photo:null, online:true }]);
      setConvs(c => {
        const existing = c[key]||[];
        // Dedup by message id — protects against duplicate delivery on reconnect.
        if (existing.some(m=>m.id===data.id)) return c;
        return { ...c, [key]: [...existing, data] };
      });
      if (chatKeyRef.current === key) socket.emit("mark-read", { peerId:data.from }); // I'm looking at this chat right now
      else setUnread(u => ({ ...u, [key]: (u[key]||0)+1 }));
    };
    // A message I sent from another tab/device of my own account.
    const onSync = (data) => {
      const key = data.groupId ? `group_${data.groupId}` : pairKey(me.id, data.to);
      setConvs(c => {
        const existing = c[key]||[];
        if (existing.some(m=>m.id===data.id)) return c;
        return { ...c, [key]: [...existing, data] };
      });
    };
    const onContactAdded = (contact) => {
      if (!contact) return;
      setContacts(prev => prev.some(c=>c.id===contact.id) ? prev : [...prev, { ...contact, online:!!onlineRef.current[contact.id] }]);
    };
    // The other person (or I, on another device) edited / deleted-for-everyone a message.
    const onMsgUpdated = ({ id, from, to, groupId, data }) => {
      const key = groupId ? `group_${groupId}` : pairKey(from, to);
      setConvs(c => c[key] ? { ...c, [key]: c[key].map(m => m.id===id ? { id:m.id, from:m.from, to:m.to, groupId:m.groupId, ts:m.ts, ...data } : m) } : c);
    };
    const onMsgHidden = ({ id, from, to, groupId }) => {
      const key = groupId ? `group_${groupId}` : pairKey(from, to);
      setConvs(c => c[key] ? { ...c, [key]: c[key].filter(m => m.id!==id) } : c);
    };
    const onGrpMsg = ({ fromName, fromRole, ...data }) => {
      // Private @mention messages are only meant for the mentioned member
      // (and the sender, who already has their own local copy). The server
      // no longer routes these to anyone else, but this check stays as a
      // safety net so a private mention can never appear in this chat,
      // in the sidebar preview, or bump the unread badge for anyone else.
      if (data.isPrivate && data.privateTo !== me.id && data.from !== me.id) return;
      const key = "group_"+data.groupId;
      setConvs(c => {
        const existing = c[key]||[];
        if (existing.some(m=>m.id===data.id)) return c;
        return { ...c, [key]: [...existing, {...data}] };
      });
      if (chatKeyRef.current === key) socket.emit("mark-read", { groupId:data.groupId }); // I'm looking at this group right now
      else setUnread(u => ({ ...u, [key]: (u[key]||0)+1 }));

      // Private @mention addressed to me, from someone else — this is the
      // ONLY case that should raise a "you were mentioned" notification.
      // Regular group messages and my own echoed private sends never do.
      if (data.isPrivate && data.privateTo === me.id && data.from !== me.id) {
        setMentionAlerts(a => ({ ...a, [key]: (a[key]||0)+1 }));
        const groupName  = groupsRef.current.find(g=>g.id===data.groupId)?.name || "the group";
        const senderName = contactsRef.current.find(c=>c.id===data.from)?.name || fromName || "Someone";
        setMentionToast({ id:data.id, groupId:data.groupId, senderName, groupName });
      }
    };
    const onOnline = (onlineUsers) => {
      onlineRef.current = onlineUsers;
      // Merge in each contact's live profile photo (broadcast by their own
      // client on "join") so their real picture shows up here instead of
      // just initials — this is what makes "the other user's image" appear.
      setContacts(prev => prev.map(c => ({
        ...c,
        online: !!onlineUsers[c.id],
        photo: onlineUsers[c.id]?.photo || c.photo || null,
      })));
    };
    const onIncoming = (data) => {
      clearTimeout(ringingRef.current);
      setCall({ visible:true, phase:"ringing", callerId:data.from, calleeId:me.id, type:data.callType||"voice", isGroup:false });
    };
    const onAccepted = () => { clearTimeout(ringingRef.current); setCall(p=>({...p,phase:"active"})); };
    const onRejected = () => { clearTimeout(ringingRef.current); setCall({visible:false}); };
    // A group call started — ring for every member of that group (not
    // just whoever's chat is open), same as WhatsApp. They get Accept /
    // Reject / Ignore.
    const onIncomingGroupCall = (data) => {
      if (data.from === me.id) return;
      setGroupCallActive(a => ({ ...a, [data.groupId]: { type:data.callType, startedBy:data.from } }));
      setCall(prev => (prev.visible ? prev : { visible:true, phase:"ringing", callerId:data.from, calleeId:me.id, type:data.callType||"voice", isGroup:true, groupId:data.groupId }));
    };
    // Whole group call session closed (last participant left) — clear the
    // "call in progress" banner everywhere, even if our own ring/overlay
    // already handled closing itself.
    const onGroupCallEndedGlobal = (data) => {
      setGroupCallActive(a => { if (!(data.groupId in a)) return a; const n={...a}; delete n[data.groupId]; return n; });
    };
    // I was added to a group, or its member list changed — keep my copy in step.
    // (The server has already put this account into the group's room.)
    const onGroupUpdated = (g) => {
      if (!g) return;
      setGroups(gs => gs.some(x => x.id === g.id) ? gs.map(x => x.id === g.id ? { ...x, ...g } : x) : [...gs, g]);
    };
    socket.on("receive-message",onMsg);
    socket.on("message-sync",onSync);
    socket.on("contact-added",onContactAdded);
    socket.on("message-updated",onMsgUpdated);
    socket.on("message-hidden",onMsgHidden);
    socket.on("receive-group-message",onGrpMsg);
    socket.on("online-users",onOnline);
    socket.on("incoming-call",onIncoming);
    socket.on("call-accepted",onAccepted);
    socket.on("call-rejected",onRejected);
    socket.on("incoming-group-call",onIncomingGroupCall);
    socket.on("group-call-ended",onGroupCallEndedGlobal);
    socket.on("group-updated",onGroupUpdated);
    return () => {
      socket.off("receive-message",onMsg);
      socket.off("message-sync",onSync);
      socket.off("contact-added",onContactAdded);
      socket.off("message-updated",onMsgUpdated);
      socket.off("message-hidden",onMsgHidden);
      socket.off("receive-group-message",onGrpMsg);
      socket.off("online-users",onOnline);
      socket.off("incoming-call",onIncoming);
      socket.off("call-accepted",onAccepted);
      socket.off("call-rejected",onRejected);
      socket.off("incoming-group-call",onIncomingGroupCall);
      socket.off("group-call-ended",onGroupCallEndedGlobal);
      socket.off("group-updated",onGroupUpdated);
    };
  }, [me]);

  useEffect(() => { const h=()=>setIsMobile(window.innerWidth<769); window.addEventListener("resize",h); return ()=>window.removeEventListener("resize",h); }, []);

  // Contacts, groups and ALL chats are NOT written here — the database owns them.
  useEffect(() => { LS.set(lsKey(me.id,"starred"),starred); }, [starred, me.id]);
  useEffect(() => { LS.set(lsKey(me.id,"pinned"),pinnedMap); }, [pinnedMap, me.id]);
  useEffect(() => { LS.set(lsKey(me.id,"trans"),translateSettings); }, [translateSettings, me.id]);
  useEffect(() => { idbGetAllRecordings().then(setCallRecordings); }, []);

  const chatKey = useMemo(() => {
    if (!me||!peer) return null;
    return peer.isGroup ? `group_${peer.id}` : pairKey(me.id, peer.id);
  }, [me, peer]);

  const currentTranslateSetting = chatKey ? (translateSettings[chatKey]||{enabled:false,targetLang:"en"}) : {enabled:false,targetLang:"en"};
  const translateEnabled = currentTranslateSetting.enabled;
  const targetLang       = currentTranslateSetting.targetLang || "en";

  const setTranslateEnabled = useCallback((val) => {
    if (!chatKey) return;
    setTranslateSettings(s => ({ ...s, [chatKey]: {...(s[chatKey]||{targetLang:"en"}), enabled:val} }));
  }, [chatKey]);

  const setTargetLang = useCallback((lang) => {
    if (!chatKey) return;
    // Clear cache for this chat when language changes so all messages re-translate
    if (convs[chatKey]) {
      convs[chatKey].forEach(msg => {
        const oldKey = `${msg.id}__${targetLang}`;
        translationCache.delete(oldKey);
      });
    }
    setTranslateSettings(s => ({ ...s, [chatKey]: {...(s[chatKey]||{enabled:false}), targetLang:lang} }));
  }, [chatKey, convs, targetLang]);

  const messages = useMemo(() => {
    if (!chatKey) return [];
    const all = convs[chatKey] || [];
    if (peer?.isGroup) return all.filter(m => !m.isPrivate || m.from===me.id || m.privateTo===me.id);
    return all;
  }, [convs, chatKey, me, peer]);

  // Same "can this person actually see it" rule as `messages` above, but
  // usable for ANY group (not just the one currently open) — used for the
  // sidebar's last-message preview so a private @mention sent to someone
  // else never leaks its text into another member's chat list.
  const lastVisibleGroupMsg = useCallback((groupId) => {
    const all = convs[`group_${groupId}`] || [];
    for (let i = all.length - 1; i >= 0; i--) {
      const m = all[i];
      if (!m.isPrivate || m.from === me.id || m.privateTo === me.id) return m;
    }
    return undefined;
  }, [convs, me]);

  // Images, videos and documents in the current chat, in order — powers prev/next navigation in the
  // viewer, WhatsApp-style. Tapping a document opens it (PDF/video preview) with a Download button,
  // so the recipient can always open or save what was sent.
  const mediaMsgs = useMemo(() => messages.filter(m => !m.uploading && (m.type==="image" || (m.type==="file" && m.fileUrl))), [messages]);
  const viewMsg = useMemo(() => mediaMsgs.find(m => m.id===viewMsgId) || null, [mediaMsgs, viewMsgId]);

  const pinnedMsg = useMemo(() => {
    if (!chatKey) return null;
    const pid = pinnedMap[chatKey];
    return pid ? (convs[chatKey]||[]).find(m=>m.id===pid) : null;
  }, [pinnedMap, chatKey, convs]);

  // Scroll to the newest message when a new one arrives or a chat is opened — but NOT when
  // older messages are paged in, or when the header's online dot changes.
  const lastMsgId = messages.length ? messages[messages.length-1].id : null;
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior:"smooth" }); }, [lastMsgId, peer?.id]);
  useEffect(() => { if (stickToBottom.current) { chatEnd.current?.scrollIntoView(); stickToBottom.current = false; } }, [convs]);
  useEffect(() => {
    chatKeyRef.current = chatKey;
    peerRef.current = peer;
    if (!chatKey) return;
    setUnread(u=>({...u,[chatKey]:0}));
    setMentionAlerts(a => (a[chatKey] ? { ...a, [chatKey]:0 } : a));
    if (peer) {
      loadChat(peer);                               // pull the complete saved history from the database
      socket.emit("mark-read", peer.isGroup ? { groupId:peer.id } : { peerId:peer.id }); // clears the unread badge on every device
    }
  }, [chatKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the open chat's header (name / online dot / photo) in step with the contact list.
  useEffect(() => {
    setPeer(p => {
      if (!p || p.isGroup) return p;
      const c = contacts.find(x => x.id === p.id);
      if (!c || (c.online===p.online && c.name===p.name && c.photo===p.photo && c.role===p.role)) return p;
      return { ...p, ...c };
    });
  }, [contacts]);

  // Starred panel can reference messages from chats not currently in memory → fetch them by id.
  useEffect(() => {
    if (!showStar) return;
    const have = new Set(Object.values(convs).flat().map(m => m.id));
    const missing = Object.keys(starred).filter(id => starred[id] && !have.has(id));
    if (!missing.length) return;
    api(`/api/messages?ids=${missing.map(encodeURIComponent).join(",")}`).then(({ messages }) => {
      setConvs(c => {
        const next = { ...c };
        messages.forEach(m => {
          const k = pairKey(m.from, m.to);
          if (!(next[k]||[]).some(x => x.id===m.id)) next[k] = [...(next[k]||[]), m];
        });
        return next;
      });
    }).catch(()=>{});
  }, [showStar]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!mentionToast) return;
    const t = setTimeout(() => setMentionToast(null), 5000);
    return () => clearTimeout(t);
  }, [mentionToast]);

  const selectPeer = useCallback(p => {
    setPeer(p);
    if (isMobile) setShowSidebar(false);
  }, [isMobile]);

  // Send a direct message to another ACCOUNT. The server saves it to the database first,
  // then pushes it to the recipient (live if online, or waiting for them when they next log in).
  // `msg.id` stays the same on retry, so the server never stores a duplicate.
  // ── File sending (up to 600 MB) ──
  // 1) show the file in the chat straight away with a progress bar  2) stream it to the server
  // 3) send the normal chat message that points at the uploaded file.  A failed upload can be retried.
  const uploadsRef = useRef({}); // messageId -> { file, abort }
  const retryFiles = useRef({}); // messageId -> File, kept so a failed upload can be retried
  const startUpload = useCallback((file, msg, key) => {
    const patch = (p) => setConvs(c => c[key] ? { ...c, [key]: c[key].map(m => m.id===msg.id ? { ...m, ...p } : m) } : c);
    patch({ uploading:true, progress:0, pending:true, failed:false, failReason:undefined });
    let last = -1;
    const { promise, abort } = uploadFile(file, pct => { if (pct !== last) { last = pct; patch({ progress:pct }); } });
    uploadsRef.current[msg.id] = { file, abort };
    retryFiles.current[msg.id] = file;
    promise.then(({ file:f }) => {
      delete uploadsRef.current[msg.id]; delete retryFiles.current[msg.id];
      const isImg = msg.type === "image";
      const wire = { ...omit(msg, ["uploading","progress","pending","failed","failReason"]),
        fileId:f.id, fileName:f.name, fileSize:fmtFileSize(f.size), fileType:f.mime,
        ...(isImg ? { imageUrl:`/files/${f.id}` } : { fileUrl:`/files/${f.id}` }) };
      if (isImg && msg.imageUrl?.startsWith("blob:")) setTimeout(() => URL.revokeObjectURL(msg.imageUrl), 5000);
      patch({ ...wire, uploading:false, progress:100, pending:true });
      deliverDirect(wire, key);
    }).catch(err => {
      delete uploadsRef.current[msg.id];
      if (err.message === "Upload cancelled") return;
      patch({ uploading:false, pending:false, failed:true, failReason:err.message });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendFileMsg = useCallback((file) => {
    if (!me||!peer||!chatKey) return;
    if (file.size === 0) { alert(`"${file.name}" is empty.`); return; }
    if (file.size > MAX_FILE_BYTES) { alert(`"${file.name}" is ${fmtFileSize(file.size)}. The maximum file size is ${Math.round(MAX_FILE_BYTES/1024/1024)} MB.`); return; }
    const isImg = file.type.startsWith("image/");
    const msg = { id:uid(), from:me.id, to:peer.id, t:ts(), ts:Date.now(), replyToId:replyTo?.id||null,
      type: isImg ? "image" : "file", fileName:file.name, fileSize:fmtFileSize(file.size), fileType:file.type,
      ...(isImg ? { imageUrl:URL.createObjectURL(file), caption:"" } : {}) };
    setReplyTo(null);
    setConvs(c => ({ ...c, [chatKey]: [...(c[chatKey]||[]), { ...msg, uploading:true, progress:0, pending:true }] }));
    startUpload(file, msg, chatKey);
  }, [me, peer, chatKey, replyTo, startUpload]);

  const deliverDirect = useCallback((msg, key) => {
    const isGroup = key.startsWith("group_");
    const evt = isGroup ? "group-message" : "send-message";
    const wire = isGroup ? { ...msg, groupId:key.slice(6) } : msg;
    socket.timeout(20000).emit(evt, wire, (err, res) => {
      setConvs(c => ({ ...c, [key]: (c[key]||[]).map(m => {
        if (m.id !== msg.id) return m;
        if (err || !res?.ok) return { ...m, pending:false, failed:true, failReason: err ? "No response from server" : res?.error };
        return { ...omit(m, ["pending","failed","failReason"]), id:res.id, ts:res.ts };
      }) }));
    });
  }, []);

  const sendMsg = useCallback(payload => {
    if (!me||!peer||!chatKey) return;
    const msg = { id:uid(), from:me.id, to:peer.id, t:ts(), ts:Date.now(), replyToId:replyTo?.id||null, ...payload };
    setReplyTo(null);
    setConvs(c => ({ ...c, [chatKey]: [...(c[chatKey]||[]), { ...msg, pending:true }] }));
    deliverDirect(msg, chatKey);
  }, [me, peer, chatKey, replyTo, deliverDirect]);

  const sendPrivateMsg = useCallback((payload, privateToIds) => {
    if (!me||!peer||!chatKey) return;
    const localId = uid();
    // The sender always sees exactly ONE bubble for what they typed —
    // multiple @mentions in the same message must not duplicate it in
    // their own chat history, even though each mentioned member gets
    // their own separately-routed private copy over the socket.
    const localMsg = { id:localId, from:me.id, to:peer.id, t:ts(), ts:Date.now(), isPrivate:true, privateTo:privateToIds[0], ...payload };
    setConvs(c => ({ ...c, [chatKey]: [...(c[chatKey]||[]), { ...localMsg, pending:true }] }));
    privateToIds.forEach((privateTo, i) => {
      const wireMsg = { id: i===0 ? localId : uid(), from:me.id, to:peer.id, t:localMsg.t, isPrivate:true, privateTo, ...payload };
      if (i===0) deliverDirect(wireMsg, chatKey);
      else socket.emit("group-message", { ...wireMsg, groupId:peer.id, privateDup:true }); // extra copy just for delivery
    });
  }, [me, peer, chatKey, deliverDirect]);

  const handleSend = useCallback(payload => {
    if (!peer?.isGroup || payload.type!=="text") { sendMsg(payload); return; }
    const mentions = [...new Set([...(payload.text?.matchAll(/@\w[\w\s]*\[([^\]]+)\]/g)||[])].map(m=>m[1]))];
    if (mentions.length>0) sendPrivateMsg(payload, mentions);
    else sendMsg(payload);
  }, [peer, sendMsg, sendPrivateMsg]);

  const addCallMsg = useCallback((withPeer,type,status,dur) => {
    if (!me||!withPeer) return;
    const k = pairKey(me.id,withPeer.id);
    const msg = { id:uid(), from:me.id, to:withPeer.id, callType:type, callStatus:status, callDur:dur||"", t:ts(), ts:Date.now() };
    setConvs(c => ({ ...c, [k]: [...(c[k]||[]), msg] }));
    socket.emit("call-log", msg); // saved to the database (only visible to me)
  }, [me]);

  const addGroupCallMsg = useCallback((groupId,type,status,dur) => {
    if (!me) return;
    const k = `group_${groupId}`;
    const msg = { id:uid(), from:me.id, to:groupId, callType:type, callStatus:status, callDur:dur||"", t:ts(), ts:Date.now() };
    setConvs(c => ({ ...c, [k]: [...(c[k]||[]), msg] }));
    socket.emit("call-log", { ...msg, groupId }); // saved in the group's history
  }, [me]);

  const addCallRecording = useCallback(rec => {
    if (!rec?.recordingUrl) return; // nothing captured (e.g. call ended too quickly) — skip
    const full = { id:uid(), t:ts(), ...rec };
    setCallRecordings(r => [...r, full]);
    idbSaveRecording(full); // persist immediately — don't rely on a later bulk save
  }, []);

  const startCall = useCallback(type => {
    if (!peer||!me) return;
    if (call.visible) return; // already in/starting a call — avoid duplicate sessions
    clearTimeout(ringingRef.current);
    const isGroup = peer.isGroup;
    if (isGroup) {
      // Starter is immediately "in" the call (matches WhatsApp) — everyone
      // else in the group gets rung and can Accept/Reject/Ignore, or join
      // later while it's still active.
      setGroupCallActive(a => ({ ...a, [peer.id]: { type, startedBy: me.id } }));
      setCall({ visible:true, phase:"active", isGroup:true, groupId:peer.id, type, callerId:me.id, calleeId:peer.id });
      socket.emit("group-call", { from:me.id, groupId:peer.id, callType:type });
      return;
    }
    setCall({ visible:true, phase:"ringing", callerId:me.id, calleeId:peer.id, type, isGroup:false });
    socket.emit("call-user",{from:me.id,to:peer.id,callType:type});
    ringingRef.current = setTimeout(()=>{
      setCall(prev=>{
        if (prev.phase!=="ringing") return prev;
        addCallMsg(peer,type,"missed","");
        setTimeout(()=>setCall({visible:false}),1500);
        return {...prev,phase:"missed"};
      });
    }, 30000);
  }, [peer, me, call.visible, addCallMsg]);

  // Join a group call already in progress (from the "call in progress"
  // banner, or after tapping Accept on an incoming ring).
  const joinGroupCall = useCallback((groupId, type, startedBy) => {
    clearTimeout(ringingRef.current);
    setCall({ visible:true, phase:"active", isGroup:true, groupId, type, callerId: startedBy||null, calleeId: groupId });
  }, []);

  const acceptCall = useCallback(()=>{
    clearTimeout(ringingRef.current);
    if (call.isGroup) { joinGroupCall(call.groupId, call.type, call.callerId); return; }
    setCall(p=>({...p,phase:"active"}));
    socket.emit("accept-call",{to:call.callerId,from:me?.id});
  }, [call, me, joinGroupCall]);

  const rejectCall = useCallback(()=>{
    clearTimeout(ringingRef.current);
    if (call.isGroup) {
      socket.emit("group-call-reject", { groupId:call.groupId, from:me?.id });
      setCall({visible:false});
      return;
    }
    const c = contacts.find(x=>x.id===call.callerId);
    socket.emit("reject-call",{to:call.callerId,from:me?.id});
    setCall({visible:false});
    if (c) addCallMsg(c,call.type,"missed","");
  }, [call, contacts, me, addCallMsg]);

  // "Ignore" (group calls only) — dismiss the ring without rejecting; the
  // call session stays alive and joinable later via the header banner.
  const ignoreCall = useCallback(()=>{
    clearTimeout(ringingRef.current);
    setCall({visible:false});
  }, []);

  // User-initiated hang up / leave — notifies the other side(s) so nobody
  // is left in a ghost "connected" state (call-sync bug fix).
  const endCall = useCallback(dur=>{
    clearTimeout(ringingRef.current);
    const wasActive = call.phase==="active";
    if (call.isGroup) {
      if (call.visible) socket.emit("group-call-leave", { groupId: call.groupId, userId: me?.id });
      setGroupCallActive(a => { if (!(call.groupId in a)) return a; const n={...a}; delete n[call.groupId]; return n; });
      setCall({visible:false});
      if (wasActive) addGroupCallMsg(call.groupId,call.type,"ended",dur||"");
    } else {
      if (call.visible) {
        const peerId = call.callerId===me?.id ? call.calleeId : call.callerId;
        socket.emit("end-call", { to:peerId, from:me?.id });
      }
      setCall({visible:false});
      const peerC = contacts.find(x=>x.id===(call.callerId===me?.id?call.calleeId:call.callerId));
      if (peerC) addCallMsg(peerC,call.type,wasActive?"ended":"cancelled",dur||"");
    }
  }, [call, me, contacts, addCallMsg, addGroupCallMsg]);

  // Remote-initiated close (peer hung up, disconnected, or the whole group
  // call session ended) — clean up locally WITHOUT re-emitting, since the
  // server already knows.
  const forceEndCall = useCallback(dur=>{
    clearTimeout(ringingRef.current);
    if (!call.visible) return;
    const wasActive = call.phase==="active";
    setCall({visible:false});
    if (call.isGroup) {
      setGroupCallActive(a => { if (!(call.groupId in a)) return a; const n={...a}; delete n[call.groupId]; return n; });
      if (wasActive) addGroupCallMsg(call.groupId,call.type,"ended",dur||"");
    } else {
      const peerC = contacts.find(x=>x.id===(call.callerId===me?.id?call.calleeId:call.callerId));
      if (peerC) addCallMsg(peerC,call.type,wasActive?"ended":"cancelled",dur||"");
    }
  }, [call, me, contacts, addCallMsg, addGroupCallMsg]);

  const buildMenuItems = useCallback(msg => {
    const isMine = msg.from===me.id;
    const items = [
      ...(msg.failed ? [{ icon:"🔁", label:"Retry sending", action:()=>retryMsg(msg) }] : []),
      { icon:"↩", label:"Reply",   action:()=>setReplyTo(msg) },
      { icon:"↪", label:"Forward", action:()=>setFwdMsg(msg) },
      { icon:"📋", label:"Copy",   action:()=>navigator.clipboard?.writeText(msg.text||"") },
      { icon:"⭐", label:starred[msg.id]?"Unstar":"Star", action:()=>setStarred(s=>({...s,[msg.id]:!s[msg.id]})) },
      { icon:"📌", label:pinnedMap[chatKey]===msg.id?"Unpin":"Pin", action:()=>setPinnedMap(p=>({...p,[chatKey]:p[chatKey]===msg.id?null:msg.id})) },
      "—",
    ];
    if (isMine&&msg.type==="text"&&!msg.callType) items.push({ icon:"✏️", label:"Edit", action:()=>setEditMsg(msg) });
    if (isMine) {
      items.push({ icon:"🗑", label:"Delete for Me",       action:()=>deleteMsg(msg.id,false) });
      items.push({ icon:"🗑", label:"Delete for Everyone", action:()=>deleteMsg(msg.id,true), danger:true });
    } else {
      items.push({ icon:"🗑", label:"Delete", action:()=>deleteMsg(msg.id,false), danger:true });
    }
    return items;
  }, [me, starred, pinnedMap, chatKey]);

  const deleteMsg = useCallback((msgId,forAll)=>{
    if (!chatKey) return;
    if (uploadsRef.current[msgId]) { uploadsRef.current[msgId].abort(); delete uploadsRef.current[msgId]; delete retryFiles.current[msgId]; }
    if (peer) socket.emit("delete-message", { id:msgId, forAll:!!forAll, groupId: peer.isGroup ? peer.id : undefined }); // persisted in the DB (+ pushed to the others)
    setConvs(c=>{
      const msgs=(c[chatKey]||[]).map(m=>{
        if (m.id!==msgId) return m;
        if (forAll) return {...m,text:"⛔ This message was deleted",type:"text",voiceUrl:undefined,imageUrl:undefined,fileUrl:undefined,deleted:true};
        return null;
      }).filter(Boolean);
      return {...c,[chatKey]:msgs};
    });
  }, [chatKey, peer]);

  const editMessage = useCallback((msgId,newText)=>{
    if (!chatKey) return;
    if (peer) socket.emit("edit-message", { id:msgId, text:newText, groupId: peer.isGroup ? peer.id : undefined });
    setConvs(c=>({...c,[chatKey]:(c[chatKey]||[]).map(m=>m.id===msgId?{...m,text:newText,edited:true}:m)}));
  }, [chatKey, peer]);

  const retryMsg = useCallback((msg)=>{
    if (!chatKey || !msg.failed) return;
    const pendingUpload = uploadsRef.current[msg.id]; // (never set for a finished upload)
    const lostFile = !msg.fileId && (msg.type==="file" || msg.type==="image") && !(msg.fileUrl||"").startsWith("/files/") && !(msg.imageUrl||"").startsWith("/files/");
    if (lostFile && !pendingUpload) {
      const f = retryFiles.current[msg.id];
      if (f) { startUpload(f, omit(msg, ["uploading","progress","pending","failed","failReason"]), chatKey); return; }
      alert("Please pick the file again to resend it."); return;
    }
    const clean = omit(msg, ["pending","failed","failReason"]);
    setConvs(c=>({...c,[chatKey]:(c[chatKey]||[]).map(m=>m.id===msg.id?{...m,pending:true,failed:false}:m)}));
    deliverDirect(clean, chatKey);
  }, [chatKey, deliverDirect]);

  const forwardTo = useCallback((destId,msg)=>{
    const isGrp = groups.some(g=>g.id===destId);
    const k = isGrp ? `group_${destId}` : pairKey(me.id,destId);
    const senderName = contacts.find(c=>c.id===msg.from)?.name||me.name;
    const base = omit(msg, ["pending","failed","failReason","isPrivate","privateTo","edited","deleted"]);
    const newMsg = {...base,id:uid(),from:me.id,to:destId,t:ts(),ts:Date.now(),forwardedFrom:senderName,replyToId:null};
    setConvs(c=>({...c,[k]:[...(c[k]||[]),{...newMsg,pending:true}]}));
    deliverDirect(newMsg, k);
  }, [me, contacts, groups, deliverDirect]);

  // Save a contact by account ID — the server confirms the ID belongs to a real user.
  const addContact = useCallback(async ({ id, name }) => {
    try {
      const { contact } = await api("/api/contacts", { method:"POST", body:{ id, name } });
      setContacts(p => p.some(x=>x.id===contact.id)
        ? p.map(x=>x.id===contact.id ? { ...x, ...contact } : x)
        : [...p, { ...contact, online:!!onlineRef.current[contact.id] }]);
      return { contact };
    } catch (e) { return { error:e.message }; }
  }, []);
  // Create a group on the server — members get it instantly (or when they next log in).
  const addGroup = useCallback(async ({ name, memberIds }) => {
    try {
      const { group } = await api("/api/groups", { method:"POST", body:{ name, memberIds } });
      setGroups(p => p.some(x=>x.id===group.id) ? p : [...p, group]);
      return { group };
    } catch (e) { return { error:e.message }; }
  },[]);
  const addMemberToGroup = useCallback(async (groupId, contactId)=>{
    try {
      const { group } = await api(`/api/groups/${encodeURIComponent(groupId)}/members`, { method:"POST", body:{ ids:[contactId] } });
      setGroups(gs => gs.map(g => g.id===group.id ? { ...g, ...group } : g));
    } catch (e) { alert(e.message); }
  },[]);
  const deleteContact = useCallback(id=>{
    api(`/api/contacts/${encodeURIComponent(id)}`, { method:"DELETE" }).catch(()=>{});
    setContacts(p=>p.filter(c=>c.id!==id));
    if(peer?.id===id) setPeer(null);
  },[peer]);
  const updateContact = useCallback(async updated=>{
    try {
      const { contact } = await api(`/api/contacts/${encodeURIComponent(updated.id)}`, { method:"PUT", body:{ name:updated.name } });
      setContacts(p=>p.map(c=>c.id===contact.id?{...c,...contact}:c));
      if (peer?.id===contact.id) setPeer(prev=>({...prev,...contact}));
      return { contact };
    } catch (e) { return { error:e.message }; }
  },[peer]);

  const visibleContacts = useMemo(()=>{
    let list = contacts;
    if (search) list=list.filter(c=>c.name.toLowerCase().includes(search.toLowerCase())||c.id.toLowerCase().includes(search.toLowerCase()));
    const lastTs = c => { const m = convs[pairKey(me.id,c.id)]; return m && m.length ? (m[m.length-1].ts||0) : 0; };
    return [...list].sort((a,b)=>lastTs(b)-lastTs(a));
  },[contacts,filter,search,convs,me.id]);

  const visibleGroups = useMemo(()=>{
    if (filter!=="All"&&filter!=="Groups") return [];
    const list = search ? groups.filter(g=>g.name?.toLowerCase().includes(search.toLowerCase())) : groups;
    const lastTs = g => { const m = convs[`group_${g.id}`]; return m && m.length ? (m[m.length-1].ts||0) : 0; };
    return [...list].sort((a,b)=>lastTs(b)-lastTs(a));
  },[groups,filter,search,convs]);

  const callerUser = call.callerId===me?.id ? me : contacts.find(c=>c.id===call.callerId);
  const calleeUser = call.calleeId===me?.id ? me : call.isGroup ? groups.find(g=>g.id===call.calleeId) : contacts.find(c=>c.id===call.calleeId);

  // peer is a snapshot taken at selection time — look the group back up
  // in the live `groups` state so member list/count stay current after
  // someone is added.
  const livePeerGroup = useMemo(()=> peer?.isGroup ? groups.find(g=>g.id===peer.id) : null, [peer, groups]);
  const groupMembers = useMemo(()=>{
    if (!peer?.isGroup) return [];
    const memberIds = livePeerGroup?.memberIds || peer.memberIds || [];
    const info = livePeerGroup?.members || [];
    return memberIds.map(id=>id===me.id?me:(contacts.find(c=>c.id===id)||info.find(m=>m.id===id))).filter(Boolean);
  },[peer,me,contacts,livePeerGroup]);

  const getSender = msg => {
    if (msg.from===me.id) return me;
    return contacts.find(c=>c.id===msg.from)
      || groups.flatMap(g=>g.members||[]).find(m=>m.id===msg.from)
      || {name:"Unknown",role:"Other"};
  };
  const getUnread = key => unread[key]||0;
  const getMentionCount = key => mentionAlerts[key]||0;
  const filterOptions = ["All","Groups"];

  const sidebarVisible = isMobile ? showSidebar : true;
  const chatVisible    = isMobile ? !showSidebar && !!peer : true;

  return (
    <div style={{ display:"flex", height:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif", background:T.slate100, overflow:"hidden" }}>

      {/* ════ SIDEBAR ════ */}
      {sidebarVisible && (
        <div style={{ width:isMobile?"100%":320, minWidth:isMobile?undefined:280, maxWidth:isMobile?undefined:340, background:T.white, borderRight:`1px solid ${T.slate200}`, display:"flex", flexDirection:"column", boxShadow:isMobile?"none":"4px 0 24px rgba(0,0,0,0.04)", flexShrink:0, zIndex:10 }}>
          <div style={{ padding:"14px 16px 10px", borderBottom:`1px solid ${T.slate100}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
              <input ref={myPhotoRef} type="file" accept="image/*" style={{ display:"none" }} onChange={changeMyPhoto} />
              <div onClick={()=>myPhotoRef.current?.click()} title="Change your profile photo" style={{ cursor:"pointer" }}>
                <Avatar user={me} size={44} online={true} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:15, color:T.slate900 }}>{me.name}</div>
                <div style={{ fontSize:12, color:T.slate400 }}>
                  <span style={{ color:(ROLE_CONFIG[me.role]||ROLE_CONFIG.Other).color, fontWeight:600 }}>{me.role}</span> · {me.id}
                </div>
              </div>
              <HdrBtn title="Open connected apps" onClick={()=>setAppHubOpen(true)}>🌐</HdrBtn>
              <HdrBtn title="Starred messages" onClick={()=>setShowStar(true)}>⭐</HdrBtn>
              <HdrBtn title="Call history" onClick={()=>setShowCallHistory(true)}>📼</HdrBtn>
              <HdrBtn title="Create group" onClick={()=>setShowGrp(true)}>👥</HdrBtn>
              <HdrBtn title="Add contact" onClick={()=>setShowAdd(true)}>➕</HdrBtn>
            </div>
            <div style={{ background:T.blue600, borderRadius:10, padding:"7px 12px", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:16 }}>💬</span>
              <span style={{ fontSize:13, color:"#fff", fontWeight:700, letterSpacing:0.5 }}>IDK</span>
              <span style={{ fontSize:10, color:"rgba(255,255,255,.75)", marginLeft:"auto", letterSpacing:1, textTransform:"uppercase" }}>Chat</span>
            </div>
          </div>
          <div style={{ padding:"10px 12px 6px", background:T.white }}>
            <div style={{ background:T.slate50, borderRadius:10, padding:"8px 12px", display:"flex", alignItems:"center", gap:8, border:`1px solid ${T.slate200}` }}>
              <span style={{ fontSize:14, color:T.slate400 }}>🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
                style={{ border:"none", outline:"none", flex:1, fontSize:13.5, color:T.slate700, background:"transparent" }} />
              {search && <button onClick={()=>setSearch("")} style={{ border:"none", background:"none", color:T.slate400, fontSize:14 }}>✕</button>}
            </div>
          </div>
          <div style={{ padding:"0 12px 8px", display:"flex", gap:6, overflowX:"auto", borderBottom:`1px solid ${T.slate100}` }}>
            {filterOptions.map(r=>(
              <button key={r} onClick={()=>setFilter(r)}
                style={{ padding:"4px 12px", borderRadius:50, border:`1.5px solid ${filter===r?T.blue500:T.slate200}`, background:filter===r?T.blue600:T.white, color:filter===r?"#fff":T.slate500, fontWeight:600, fontSize:11.5, whiteSpace:"nowrap", flexShrink:0 }}>
                {r}
              </button>
            ))}
          </div>
          <div style={{ flex:1, overflowY:"auto" }}>
            {(filter==="All"||filter==="Groups") && visibleGroups.map(g=>(
              <ConvRow key={g.id} contact={{id:g.id,name:g.name,role:"Group"}}
                lastMsg={lastVisibleGroupMsg(g.id)}
                unread={getUnread(`group_${g.id}`)}
                mentionCount={getMentionCount(`group_${g.id}`)}
                active={peer?.id===g.id&&peer?.isGroup}
                onClick={()=>selectPeer({...g,isGroup:true})}
                isGroup />
            ))}
            {filter!=="Groups" && visibleContacts.map(c=>{
              const k=pairKey(me.id,c.id);
              const msgs=convs[k]||[];
              return <ConvRow key={c.id} contact={c} lastMsg={msgs[msgs.length-1]} unread={getUnread(k)} active={peer?.id===c.id&&!peer?.isGroup} onClick={()=>selectPeer(c)} />;
            })}
            {visibleContacts.length===0 && visibleGroups.length===0 && (
              <div style={{ padding:"48px 20px", textAlign:"center" }}>
                <div style={{ width:56, height:56, borderRadius:16, background:T.slate100, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, margin:"0 auto 14px" }}>👥</div>
                <div style={{ fontSize:14.5, fontWeight:600, color:T.slate700, marginBottom:6 }}>No contacts yet</div>
                <div style={{ fontSize:13, color:T.slate400 }}>Tap <strong style={{ color:T.blue600 }}>+</strong> to add contacts</div>
              </div>
            )}
          </div>
          <div style={{ padding:"10px 14px", borderTop:`1px solid ${T.slate100}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:T.success }} />
              <span style={{ fontSize:12, color:T.slate500 }}>{me.name}</span>
            </div>
            <button onClick={()=>{ stopWebRTCCall(); setPeer(null); onLogout(); }}
              style={{ background:"none", border:`1px solid ${T.slate200}`, borderRadius:7, color:T.slate500, fontSize:12, padding:"4px 10px" }}
              onMouseEnter={e=>{ e.currentTarget.style.background=T.dangerBg; e.currentTarget.style.color=T.danger; e.currentTarget.style.borderColor=T.danger; }}
              onMouseLeave={e=>{ e.currentTarget.style.background="none"; e.currentTarget.style.color=T.slate500; e.currentTarget.style.borderColor=T.slate200; }}>
              Log out
            </button>
          </div>
        </div>
      )}

      {/* ════ MAIN CHAT AREA ════ */}
      {(!isMobile||chatVisible) && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, background:T.slate50 }}>
          {peer ? (
            <>
              {/* Chat Header */}
              <div style={{ background:T.white, borderBottom:`1px solid ${T.slate200}`, padding:"10px 16px", display:"flex", alignItems:"center", gap:12, boxShadow:"0 2px 12px rgba(0,0,0,0.04)", zIndex:5, flexShrink:0 }}>
                {isMobile && (
                  <button onClick={()=>{ setPeer(null); setShowSidebar(true); }}
                    style={{ background:"none", border:"none", fontSize:22, color:T.blue600, padding:"0 4px", marginLeft:-4 }}>←</button>
                )}
                {peer.isGroup ? <Avatar group size={42} /> : <Avatar user={peer} size={42} online={peer.online} />}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:15.5, color:T.slate900 }}>{peer.name}</div>
                  <div style={{ fontSize:11.5, color:T.slate400, marginTop:1, cursor:"pointer" }}
                    onClick={()=>{ if (peer.isGroup) setShowGroupInfo(true); else setShowContactMedia(true); }}>
                    {peer.isGroup ? `${groupMembers.length} members` : `${peer.role} · ${peer.id} · ${peer.online?"🟢 Online":"⚫ Offline"}`}
                  </div>
                </div>
                <HdrBtn title="Camera" onClick={()=>setShowCam(true)}>📷</HdrBtn>
                {!peer.isGroup && <HdrBtn title="Media, links and docs" onClick={()=>setShowContactMedia(true)}>🖼️</HdrBtn>}
                <HdrBtn title="Voice call" onClick={()=>startCall("voice")}>📞</HdrBtn>
                <HdrBtn title="Video call" onClick={()=>startCall("video")}>📹</HdrBtn>
                {!peer.isGroup && <>
                  <HdrBtn title="Edit contact" onClick={()=>setEditContact(peer)}>✏️</HdrBtn>
                  <HdrBtn title="Remove contact" onClick={()=>{ if(window.confirm(`Remove ${peer.name}?`)) deleteContact(peer.id); }}>🗑</HdrBtn>
                </>}
              </div>

              {/* Group call in progress banner — lets anyone join anytime
                  while it's active, even if they Ignored the original ring. */}
              {peer.isGroup && groupCallActive[peer.id] && !(call.visible && call.isGroup && call.groupId===peer.id) && (
                <div style={{ background:T.successBg, borderBottom:`1px solid #A7F3D0`, padding:"8px 16px", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                  <span style={{ width:8, height:8, borderRadius:"50%", background:T.success, display:"inline-block", animation:"pulse 1.4s infinite" }} />
                  <div style={{ flex:1, fontSize:13, color:"#065F46", fontWeight:600 }}>
                    {groupCallActive[peer.id].type==="video"?"📹":"📞"} Group call in progress
                  </div>
                  <button onClick={()=>joinGroupCall(peer.id, groupCallActive[peer.id].type, groupCallActive[peer.id].startedBy)}
                    style={{ background:T.success, border:"none", borderRadius:8, color:"#fff", fontSize:12.5, fontWeight:700, padding:"6px 14px" }}>
                    Join
                  </button>
                </div>
              )}

              {/* Translation Bar */}
              <TranslationBar targetLang={targetLang} onChangeLang={setTargetLang} enabled={translateEnabled} onToggle={()=>setTranslateEnabled(!translateEnabled)} />

              {/* Pinned message */}
              {pinnedMsg && (
                <div style={{ background:T.blue50, borderBottom:`1px solid ${T.blue200}`, padding:"7px 16px", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                  <span style={{ fontSize:14, color:T.blue500 }}>📌</span>
                  <div style={{ flex:1, fontSize:13, color:T.slate600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    <strong style={{ color:T.blue600 }}>Pinned:</strong> {stripMentionTags(pinnedMsg.text)||(pinnedMsg.type==="voice"?"🎤 Voice":pinnedMsg.type==="image"?"📷 Image":"📎 File")}
                  </div>
                  <button onClick={()=>setPinnedMap(p=>({...p,[chatKey]:null}))} style={{ background:"none", border:"none", color:T.slate400, fontSize:14 }}>✕</button>
                </div>
              )}


              {/* Messages */}
              <div style={{ flex:1, overflowY:"auto", overflowX:"hidden", minWidth:0, padding:"16px 16px", display:"flex", flexDirection:"column", gap:2 }}>
                {messages.length===0 && (
                  <div style={{ display:"flex", justifyContent:"center", marginTop:24 }}>
                    <div style={{ background:T.white, borderRadius:14, padding:"14px 24px", fontSize:13, color:T.slate400, textAlign:"center", border:`1px solid ${T.slate200}` }}>
                      🔒 End-to-end encrypted<br />
                      <span style={{ fontSize:12, opacity:0.7 }}>Start a conversation</span>
                    </div>
                  </div>
                )}
                {hasMore[chatKey] && (
                  <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
                    <button onClick={()=>loadOlder(peer)} style={{ background:T.white, border:`1px solid ${T.slate200}`, borderRadius:20, padding:"6px 16px", fontSize:12.5, color:T.blue600, fontWeight:600 }}>
                      ↑ Load earlier messages
                    </button>
                  </div>
                )}
                {messages.map((msg,i)=>{
                  const isMine = msg.from === me.id;
                  const prevMsg = messages[i-1];
                  const showDay = !!msg.ts && (!prevMsg?.ts || dayKey(prevMsg.ts)!==dayKey(msg.ts));
                  const sender = getSender(msg);
                  const replyMsg = msg.replyToId ? (convs[chatKey]||[]).find(m=>m.id===msg.replyToId) : null;
                  const replySender = replyMsg ? getSender(replyMsg) : null;
                  return (
                    <Fragment key={msg.id}>
                    {showDay && (
                      <div style={{ display:"flex", justifyContent:"center", margin:"10px 0 6px" }}>
                        <span style={{ background:T.white, border:`1px solid ${T.slate200}`, borderRadius:10, padding:"3px 12px", fontSize:11.5, color:T.slate500, fontWeight:600 }}>{dayLabel(msg.ts)}</span>
                      </div>
                    )}
                    <Bubble msg={msg}
                      isMine={isMine}
                      senderName={sender.name}
                      senderColor={(ROLE_CONFIG[sender.role]||ROLE_CONFIG.Other).color}
                      replyMsg={replyMsg}
                      replySenderName={replySender?.name}
                      isStarred={!!starred[msg.id]}
                      translateEnabled={translateEnabled}
                      targetLang={targetLang}
                      onCtx={(e,m)=>setCtxMenu({x:Math.min(e.clientX,window.innerWidth-200),y:Math.min(e.clientY,window.innerHeight-320),msg:m})}
                      onView={m=>setViewMsgId(m.id)}
                    />
                    </Fragment>
                  );
                })}
                <div ref={chatEnd} />
              </div>

              <InputBar onSend={handleSend} onSendFile={sendFileMsg} replyTo={replyTo} replyName={replyTo?getSender(replyTo).name:null} onCancelReply={()=>setReplyTo(null)} members={peer.isGroup?groupMembers.filter(m=>m.id!==me.id):null} targetLang={targetLang} translateEnabled={translateEnabled} onOpenCamera={()=>setShowCam(true)} />
            </>
          ) : (
            !isMobile && (
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ textAlign:"center", maxWidth:400, padding:40 }}>
                  <div style={{ width:80, height:80, borderRadius:22, background:T.blue600, display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, margin:"0 auto 24px", boxShadow:`0 8px 28px rgba(37,99,235,0.3)` }}>💬</div>
                  <div style={{ fontSize:28, fontWeight:700, color:T.slate900, fontFamily:"'Instrument Serif',serif", marginBottom:8 }}>IDK</div>
                  <div style={{ width:48, height:3, background:T.blue500, borderRadius:3, margin:"0 auto 16px" }} />
                  <div style={{ fontSize:14.5, color:T.slate500, lineHeight:1.7, marginBottom:24 }}>
                    Select a contact to start a conversation.
                  </div>
                  <div style={{ marginTop:16, display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
                    {["💬 Direct messages","👥 Group chats","📎 File sharing","📞 Voice & video calls","🌐 Translation"].map(t=>(
                      <div key={t} style={{ padding:"7px 14px", background:T.white, borderRadius:10, border:`1px solid ${T.slate200}`, fontSize:12, color:T.slate500 }}>{t}</div>
                    ))}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* ════ OVERLAYS ════ */}
      
      {call.visible && !call.isGroup && <CallOverlay callState={call} callerUser={callerUser} calleeUser={calleeUser} meId={me.id} onAccept={acceptCall} onReject={rejectCall} onEnd={endCall} onForceEnd={forceEndCall} onRecordingReady={addCallRecording} />}
      {call.visible && call.isGroup && <GroupCallOverlay callState={call} me={me} contacts={contacts} groups={groups} onAccept={acceptCall} onReject={rejectCall} onIgnore={ignoreCall} onEnd={endCall} onForceEnd={forceEndCall} onRecordingReady={addCallRecording} />}
      {showCam && <CameraCapture onSend={handleSend} onClose={()=>setShowCam(false)} />}
      {showAdd && <AddContactModal onSave={addContact} onClose={()=>setShowAdd(false)} />}
      {showGrp && <CreateGroupModal contacts={contacts} me={me} onSave={addGroup} onClose={()=>setShowGrp(false)} />}
      {viewMsg && <MediaViewer msg={viewMsg} items={mediaMsgs} onNavigate={setViewMsgId} onClose={()=>setViewMsgId(null)} />}
      {showGroupInfo && livePeerGroup && <GroupInfoModal group={livePeerGroup} members={groupMembers} contacts={contacts} me={me} messages={messages} onAddMember={cid=>addMemberToGroup(livePeerGroup.id,cid)} onClose={()=>setShowGroupInfo(false)} onViewMedia={id=>setViewMsgId(id)} />}
      {showContactMedia && peer && !peer.isGroup && <ContactMediaModal contact={peer} messages={messages} onClose={()=>setShowContactMedia(false)} onViewMedia={id=>setViewMsgId(id)} />}
      {showStar && <StarredPanel starred={starred} allConvs={convs} contacts={contacts} me={me} onClose={()=>setShowStar(false)} />}
      {showCallHistory && <CallHistoryModal recordings={callRecordings} onClose={()=>setShowCallHistory(false)} />}
      {editContact && <EditContactModal contact={editContact} onSave={updated=>{ updateContact(updated); setEditContact(null); }} onClose={()=>setEditContact(null)} />}
      {ctxMenu && <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={buildMenuItems(ctxMenu.msg)} onClose={()=>setCtxMenu(null)} />}
      {mentionToast && (
        <div onClick={()=>{
            const g = groups.find(g=>g.id===mentionToast.groupId);
            if (g) selectPeer({...g,isGroup:true});
            setMentionToast(null);
          }}
          style={{ position:"fixed", top:16, right:16, zIndex:2000, background:T.slate900, color:"#fff", borderRadius:14, padding:"12px 16px", boxShadow:"0 10px 30px rgba(0,0,0,0.25)", display:"flex", alignItems:"center", gap:10, maxWidth:320, cursor:"pointer", animation:"slideIn .18s ease" }}>
          <div style={{ width:34, height:34, borderRadius:"50%", background:T.violet, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>@</div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700 }}>You were mentioned</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.75)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {mentionToast.senderName} privately mentioned you in {mentionToast.groupName}
            </div>
          </div>
          <button onClick={e=>{ e.stopPropagation(); setMentionToast(null); }} style={{ background:"none", border:"none", color:"rgba(255,255,255,.6)", fontSize:14, marginLeft:4 }}>✕</button>
        </div>
      )}
      {editMsg && <EditModal msg={editMsg} onSave={text=>editMessage(editMsg.id,text)} onClose={()=>setEditMsg(null)} />}
      {fwdMsg && <ForwardModal msg={fwdMsg} contacts={contacts} groups={groups} me={me} onForward={destId=>forwardTo(destId,fwdMsg)} onClose={()=>setFwdMsg(null)} />}
      {appHubOpen && <ExternalAppsHub onClose={()=>setAppHubOpen(false)} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   APP — login gate. Everything after login lives in <Messenger/>.
══════════════════════════════════════════════════════════ */
export default function App() {
  const [me, setMe]         = useState(null);
  const [booting, setBooting] = useState(() => !!getToken());
  const [bootErr, setBootErr] = useState("");
  const [gateOpen, setGateOpen] = useState(null); // null = checking, false = locked, true = passed (or gate is off)

  const checkGate = useCallback(() => {
    fetch(SERVER_URL + "/api/gate/status", { headers: { "X-Gate-Token": getGate() } })
      .then(r => r.json()).then(d => setGateOpen(!d.required || d.ok))
      .catch(() => setGateOpen(false));
  }, []);
  useEffect(() => {
    checkGate();
    const lock = () => { clearGate(); disconnectSocket(); setGateOpen(false); };
    window.addEventListener("mc3-gate-required", lock);
    window.addEventListener("mc3-gate-check", checkGate);
    return () => { window.removeEventListener("mc3-gate-required", lock); window.removeEventListener("mc3-gate-check", checkGate); };
  }, [checkGate]);

  const restoreSession = useCallback(() => {
    if (!getToken()) { setBooting(false); return; }
    setBooting(true); setBootErr("");
    api("/api/me")
      .then(({ user }) => { setMe(user); connectSocket(); })
      .catch(e => { if (getToken()) setBootErr(e.message); }) // 401 is handled by the "mc3-unauthorized" listener below
      .finally(() => setBooting(false));
  }, []);

  // Reopening the app / refreshing: the saved login token brings the user straight back in.
  useEffect(() => { if (gateOpen) restoreSession(); }, [restoreSession, gateOpen]);

  // Session expired / token rejected → back to the login screen.
  useEffect(() => {
    const h = () => { clearToken(); disconnectSocket(); setMe(null); setBooting(false); setBootErr(""); };
    window.addEventListener("mc3-unauthorized", h);
    return () => window.removeEventListener("mc3-unauthorized", h);
  }, []);

  const handleAuth = useCallback(user => { setMe(user); disconnectSocket(); connectSocket(); }, []);

  const logout = useCallback(async () => {
    try { await api("/api/logout", { method:"POST" }); } catch {}
    clearToken(); disconnectSocket(); setMe(null);
  }, []);

  if (gateOpen === null) return <div style={{ minHeight:"100vh", background:"#000" }} />;
  if (!gateOpen) return <Gate onPass={() => setGateOpen(true)} />;
  if (booting) return <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans',sans-serif", color:T.slate500 }}>Loading IDK…</div>;
  if (bootErr) return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", gap:14, alignItems:"center", justifyContent:"center", fontFamily:"'Plus Jakarta Sans',sans-serif", color:T.slate600, padding:20, textAlign:"center" }}>
      <div style={{ fontSize:15 }}>⚠️ {bootErr}</div>
      <button onClick={restoreSession} style={{ padding:"10px 22px", borderRadius:10, border:"none", background:T.blue600, color:"#fff", fontWeight:700 }}>Try again</button>
    </div>
  );
  if (!me) return <AuthScreen onAuth={handleAuth} />;
  return <Messenger key={me.id} me={me} setMe={setMe} onLogout={logout} />;
}